import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import AdminLayout from "../../../layouts/AdminLayout";
import {
  Button,
  Modal,
  FormField,
  Textarea,
  SelectDropdown,
  SearchInput,
  LoadingSkeleton,
} from "../../../components/ui";
import TowerForm from "../../../components/societies/TowerForm";
import UnitForm from "../../../components/societies/UnitForm";
import SocietyForm from "../../../components/societies/SocietyForm";
import PropertyDocuments from "../../../components/societies/PropertyDocuments";
import PropertyApprovals from "../../../components/societies/PropertyApprovals";
import PropertyDirectories from "../../../components/societies/PropertyDirectories";
import PropertyGates from "../../../components/societies/PropertyGates";
import PropertyStaff from "../../../components/societies/PropertyStaff";
import InviteUserModal from "../../../components/users/InviteUserModal";
import { Link2, Plus, Building, Upload } from "lucide-react";
import PropertyCommittee from "../../../components/societies/PropertyCommittee";
import PropertySettings from "../../../components/societies/PropertySettings";
import PropertyDeliveryCabNetwork from "../../../components/societies/PropertyDeliveryCabNetwork";
import PropertyIndustrialSettings from "../../../components/societies/PropertyIndustrialSettings";
import PropertyVisitorPasses from "../../../components/societies/PropertyVisitorPasses";
import PropertyTesters from "../../../components/societies/PropertyTesters";
import CommunityMessages from "../../Community/messages/CommunityMessages";
import Support from "../../SupportTicket/admin/Support";
import BulkImport from "../../../components/BulkImport";
import styles from "./PropertyExplorer.module.css";
import {
  Building2,
  ChevronLeft,
  Laptop,
  Users,
  Store,
  ArrowRight,
  DoorOpen,
  Lock,
  UserPlus,
  RefreshCw,
  UserMinus,
  Trash2,
} from "lucide-react";
import { generateWorkspaceCode, getPropertyPrefix, getDepartmentCode } from "../../../utils/codeGenerator";

import { getSocieties, getTowersBySociety, getPendingApprovals, getTowerConfigs } from "../../../services/societyService";
import { resolveBackendUrl } from "../../../services/api";
import { getFlatsBySociety, getFlatById, updateFlatStructure, assignStaffToFlat, unassignStaffFromFlat } from "../../../services/flatsService";
import { getSocietyStaff } from "../../../services/societyService";

import { mergeUnits, unmergeUnits } from "../../../services/unitService";
import { getCurrentRoleKey } from "../../../services/authService";
import masterService from "../../../services/masterEntitiesService";
import { toast } from "react-toastify";
import {
  formatFloorName,
  getUnitTypeBadgeStyle,
  normalizePropertyType,
  PROPERTY_CATEGORIES,
  getDepartmentShortCode,
  getPropertyNavigationTabs,
} from "../../../utils/propertyStructure";

const isDepartmentAnchorUnit = (u, deptName, deptCode, propPrefix) => {
  const num = String(u?.flat_number || "").trim().toLowerCase();
  const dCode = String(deptCode || "").trim().toLowerCase();
  const dName = String(deptName || "").trim().toLowerCase();
  const prefix = String(propPrefix || "").trim().toLowerCase();

  // If it has desk/cabin indicators, it is definitely a desk/cabin, NOT an anchor:
  if (
    num.includes("-d") ||
    num.includes("-c") ||
    num.includes("-s") ||
    num.startsWith("d-") ||
    num.startsWith("c-") ||
    /\b(desk|cabin|seat|workstation)\b/i.test(num)
  ) {
    return false;
  }

  // Exact anchor match:
  // e.g. "ah-ops", "ops", "ah-operations", "operations", or matches `${propPrefix}-${deptCode}`
  if (dCode && (num === dCode || (prefix && num === `${prefix}-${dCode}`))) {
    return true;
  }
  if (dName && (num === dName || (prefix && num === `${prefix}-${dName}`))) {
    return true;
  }

  // Also check if flat_number contains no digits at all (e.g. "hr", "it", "ops", "ah-mkt", "sales")
  if (!/\d/.test(num) && (num.includes(dCode) || (dName && num.includes(dName)))) {
    return true;
  }

  return false;
};

const PropertyExplorer = () => {
  const roleKey = getCurrentRoleKey();
  const isSuperAdmin = roleKey === "super-admin";
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const propertyId = searchParams.get("propertyId") || "";
  const towerId = searchParams.get("towerId") || "";
  const tabFromUrl = searchParams.get("tab") || "towers";

  const [properties, setProperties] = useState([]);
  const [towers, setTowers] = useState([]);
  const [flats, setFlats] = useState([]);
  const [towerConfigs, setTowerConfigs] = useState([]);

  const [loadingProperties, setLoadingProperties] = useState(true);
  const [loadingDetails, setLoadingDetails] = useState(false);

  // Pagination states
  const [propLimit, setPropLimit] = useState(50);
  const [propOffset, setPropOffset] = useState(0);

  const [towerLimit, setTowerLimit] = useState(50);
  const [towerOffset, setTowerOffset] = useState(0);

  const [floorLimit, setFloorLimit] = useState(5);
  const [floorOffset, setFloorOffset] = useState(0);

  const floorFromUrl = searchParams.get("floor") || "";
  const deskFromUrl = searchParams.get("desk") || searchParams.get("search") || "";

  const [searchQuery, setSearchQuery] = useState(deskFromUrl);
  const [propertyTypeFilter, setPropertyTypeFilter] = useState("");
  const [flatTypeFilter, setFlatTypeFilter] = useState("");
  const [floorFilter, setFloorFilter] = useState(floorFromUrl);

  useEffect(() => {
    const f = searchParams.get("floor");
    if (f !== null && f !== undefined) setFloorFilter(f);
    const d = searchParams.get("desk") || searchParams.get("search");
    if (d !== null && d !== undefined) setSearchQuery(d);
  }, [searchParams]);

  const [mergeMode, setMergeMode] = useState(false);
  const [mergeFloorId, setMergeFloorId] = useState(null);
  const [mergeSelected, setMergeSelected] = useState(new Set());

  const [towerModalOpen, setTowerModalOpen] = useState(false);
  const [unitModalOpen, setUnitModalOpen] = useState(false);
  const [unitModalInitialFloor, setUnitModalInitialFloor] = useState(null);
  const [unitModalInitialDept, setUnitModalInitialDept] = useState("");
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [propertyModalOpen, setPropertyModalOpen] = useState(false);
  const [propertyEditing, setPropertyEditing] = useState(null);

  const [addDeptModalOpen, setAddDeptModalOpen] = useState(false);
  const [newDeptName, setNewDeptName] = useState("");
  const [newDeptDescription, setNewDeptDescription] = useState("");
  const [newDeptTowerId, setNewDeptTowerId] = useState("");
  const [newDeptFloorNumber, setNewDeptFloorNumber] = useState("0");
  const [newDeptCode, setNewDeptCode] = useState("");
  const [masterDepartmentsList, setMasterDepartmentsList] = useState([]);

  const [savingDept, setSavingDept] = useState(false);
  const [pendingApprovalsCount, setPendingApprovalsCount] = useState(0);

  const [unitDetailsModalOpen, setUnitDetailsModalOpen] = useState(false);
  const [assignDeskModalOpen, setAssignDeskModalOpen] = useState(false);
  const [shiftDeskModalOpen, setShiftDeskModalOpen] = useState(false);
  const [staffToShift, setStaffToShift] = useState(null);
  const [targetShiftFlatId, setTargetShiftFlatId] = useState("");
  const [staffUserIdToAssign, setStaffUserIdToAssign] = useState("");
  const [assignDepartment, setAssignDepartment] = useState("");
  const [availableSocietyStaff, setAvailableSocietyStaff] = useState([]);
  const [savingDeskAction, setSavingDeskAction] = useState(false);
  const [unitDetailsLoading, setUnitDetailsLoading] = useState(false);
  const [unitDetails, setUnitDetails] = useState(null);
  const [unitDetailsError, setUnitDetailsError] = useState("");
  const [selectedUnitIdForDetails, setSelectedUnitIdForDetails] = useState("");
  const [selectedDeptOnFloor, setSelectedDeptOnFloor] = useState({});

  const viewLevel = useMemo(() => {
    if (towerId) return 3; // View units
    if (propertyId) return 2; // View towers/documents
    return 1; // View properties
  }, [propertyId, towerId]);

  const [activeTab, setActiveTab] = useState(tabFromUrl);
  const [inviteModalOpen, setInviteModalOpen] = useState(false);

  useEffect(() => {
    const currentTab = searchParams.get("tab") || "towers";
    setActiveTab(currentTab);
  }, [searchParams]);

  const selectedProperty = useMemo(
    () => properties.find((p) => String(p.society_id) === String(propertyId)) || null,
    [properties, propertyId]
  );

  useEffect(() => {
    if (!newDeptName || !newDeptName.trim()) {
      setNewDeptCode("");
      return;
    }
    const propPrefix = getPropertyPrefix(selectedProperty?.society_name || "");
    const deptShort = getDepartmentCode(newDeptName.trim());
    const code = propPrefix ? `${propPrefix}-${deptShort}` : deptShort;
    setNewDeptCode(code);
  }, [newDeptName, selectedProperty?.society_name]);

  const propertyCategory = useMemo(
    () => normalizePropertyType(selectedProperty?.society_type),
    [selectedProperty]
  );
  const isIndustrial = propertyCategory === PROPERTY_CATEGORIES.INDUSTRIAL;
  const isCommercial = propertyCategory === PROPERTY_CATEGORIES.COMMERCIAL;

  const propertyTabs = useMemo(() => {
    return getPropertyNavigationTabs(selectedProperty?.society_type, {
      approvals: pendingApprovalsCount,
    });
  }, [selectedProperty, pendingApprovalsCount]);

  const selectedTower = useMemo(
    () => towers.find((t) => String(t.tower_id) === String(towerId)) || null,
    [towers, towerId]
  );

  const fetchProperties = useCallback(async () => {
    setLoadingProperties(true);
    try {
      const data = await getSocieties();
      setProperties(Array.isArray(data) ? data : []);
    } finally {
      setLoadingProperties(false);
    }
  }, []);

  const fetchPropertyDetails = useCallback(async () => {
    if (!propertyId) {
      setTowers([]);
      setFlats([]);
      setPendingApprovalsCount(0);
      return;
    }
    setLoadingDetails(true);
    try {
      const [towerData, flatData, approvalsData, configsData] = await Promise.all([
        getTowersBySociety(propertyId),
        getFlatsBySociety(propertyId),
        getPendingApprovals(propertyId).catch(() => []),
        getTowerConfigs(propertyId).catch(() => []),
      ]);
      setTowers(Array.isArray(towerData) ? towerData : []);
      setFlats(Array.isArray(flatData) ? flatData : []);
      setTowerConfigs(
        Array.isArray(configsData)
          ? configsData
          : Array.isArray(configsData?.towers)
            ? configsData.towers
            : []
      );
      const count = Array.isArray(approvalsData)
        ? approvalsData.length
        : approvalsData?.data?.length || 0;
      setPendingApprovalsCount(count);
    } finally {
      setLoadingDetails(false);
    }
  }, [propertyId]);


  useEffect(() => {
    const loadMasterDepts = async () => {
      try {
        const data = await masterService.getMasterDepartmentTypes();
        if (Array.isArray(data)) {
          setMasterDepartmentsList(data);
        }
      } catch (e) {
        // Fallback gracefully
      }
    };
    loadMasterDepts();
  }, []);

  useEffect(() => {
    fetchProperties();
  }, [fetchProperties]);

  useEffect(() => {
    fetchPropertyDetails();
  }, [fetchPropertyDetails]);

  // Reset pagination when search changes
  useEffect(() => {
    setPropOffset(0);
    setTowerOffset(0);
    setFloorOffset(0);
  }, [searchQuery, flatTypeFilter, floorFilter]);

  // Reset filters and merge when switching context
  useEffect(() => {
    setMergeSelected(new Set());
    setMergeFloorId(null);
    setSearchQuery("");
    setFlatTypeFilter("");
    setFloorFilter("");
    setSelectedDeptOnFloor({});
    const tabInUrl = searchParams.get("tab");
    if (tabInUrl) {
      setActiveTab(tabInUrl);
    } else {
      setActiveTab("towers");
    }
  }, [propertyId, towerId]);

  const typeQueryParam = searchParams.get("type") || "";

  useEffect(() => {
    if (typeQueryParam) {
      setPropertyTypeFilter(typeQueryParam);
    } else {
      setPropertyTypeFilter("");
    }
  }, [typeQueryParam]);

  const availablePropertyTypes = useMemo(() => {
    const types = new Set();
    properties.forEach((p) => {
      if (p.society_type) types.add(p.society_type);
    });
    return Array.from(types).sort();
  }, [properties]);

  const filteredProperties = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    let result = properties;

    if (propertyTypeFilter) {
      const filterLower = propertyTypeFilter.trim().toLowerCase();
      result = result.filter(
        (p) => String(p.society_type || "").trim().toLowerCase() === filterLower
      );
    }

    if (q) {
      result = result.filter((p) =>
        String(p.society_name || "").toLowerCase().includes(q)
      );
    }
    return result;
  }, [properties, searchQuery, propertyTypeFilter]);

  const displayedProperties = useMemo(() => {
    return filteredProperties.slice(propOffset, propOffset + propLimit);
  }, [filteredProperties, propOffset, propLimit]);

  const filteredTowers = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return towers;
    return towers.filter((t) =>
      String(t.tower_name || "").toLowerCase().includes(q)
    );
  }, [towers, searchQuery]);

  const displayedTowers = useMemo(() => {
    return filteredTowers.slice(towerOffset, towerOffset + towerLimit);
  }, [filteredTowers, towerOffset, towerLimit]);

  const availableFlatTypes = useMemo(() => {
    if (!towerId) return [];
    const types = new Set();
    flats.filter(f => String(f.tower_id) === String(towerId)).forEach((f) => {
      if (f.unit_type) types.add(f.unit_type);
    });
    return Array.from(types).sort();
  }, [flats, towerId]);

  const availableFloors = useMemo(() => {
    if (!towerId) return [];
    const fls = new Set();
    flats.filter(f => String(f.tower_id) === String(towerId)).forEach((f) => {
      if (f.floor_number !== undefined && f.floor_number !== null) fls.add(String(f.floor_number));
    });
    return Array.from(fls).sort((a, b) => Number(a) - Number(b));
  }, [flats, towerId]);

  const towerUnits = useMemo(() => {
    if (!towerId) return [];
    const q = searchQuery.trim().toLowerCase();

    return flats
      .filter((f) => String(f.tower_id) === String(towerId))
      .filter((f) => {
        if (flatTypeFilter && String(f.unit_type) !== flatTypeFilter) return false;
        if (floorFilter && String(f.floor_number) !== floorFilter) return false;

        const unitType = String(f.unit_type || "").trim();
        const isMergedDisplay = unitType === "Jodi" || unitType === "MERGED";
        if (Boolean(f.is_merge_member) && !isMergedDisplay) return false;

        const hasMergeMarker =
          Boolean(f.merged_unit_id) ||
          Boolean(f.is_merged) ||
          (f.merged_from != null && String(f.merged_from).trim() !== "");
        if (hasMergeMarker && !isMergedDisplay) return false;
        return true;
      })
      .filter((f) => {
        if (!q) return true;
        return (
          String(f.flat_number || "").toLowerCase().includes(q) ||
          String(f.unit_type || "").toLowerCase().includes(q) ||
          String(f.floor_number || "").toLowerCase().includes(q)
        );
      });
  }, [flats, towerId, searchQuery, flatTypeFilter, floorFilter]);

  const unitsByFloor = useMemo(() => {
    const map = new Map();
    for (const unit of towerUnits) {
      const floor = Number(unit.floor_number);
      if (!map.has(floor)) map.set(floor, []);
      map.get(floor).push(unit);
    }
    for (const [floor, list] of map.entries()) {
      list.sort((a, b) =>
        String(a.flat_number).localeCompare(String(b.flat_number), undefined, { numeric: true })
      );
      map.set(floor, list);
    }
    return map;
  }, [towerUnits]);

  const currentTowerConfig = useMemo(() => {
    if (!towerId) return null;
    return (towerConfigs || []).find((tc) => String(tc.tower_id) === String(towerId));
  }, [towerConfigs, towerId]);

  const allTowerFloors = useMemo(() => {
    if (!towerId) return [];
    const set = new Set();

    // 1. From backend tower configs floors list
    if (Array.isArray(currentTowerConfig?.floors)) {
      currentTowerConfig.floors.forEach((f) => {
        const fn = Number(typeof f === "object" ? f.floor_number : f);
        if (Number.isInteger(fn)) set.add(fn);
      });
    }

    // 2. From actual flats on this tower
    flats
      .filter((f) => String(f.tower_id) === String(towerId))
      .forEach((f) => {
        if (f.floor_number !== undefined && f.floor_number !== null) {
          set.add(Number(f.floor_number));
        }
      });

    // 3. From total_floors definition if set is still empty
    if (set.size === 0 && currentTowerConfig?.total_floors) {
      const tf = Number(currentTowerConfig.total_floors);
      const sf = Number(currentTowerConfig.start_floor ?? (isIndustrial ? 0 : 1));
      for (let i = 0; i < tf; i++) {
        set.add(sf + i);
      }
    }

    return Array.from(set).sort((a, b) => a - b);
  }, [currentTowerConfig, flats, towerId, isIndustrial]);

  const floors = useMemo(() => {
    if (floorFilter) {
      return allTowerFloors.filter((fl) => String(fl) === String(floorFilter));
    }
    if (searchQuery.trim() || flatTypeFilter) {
      return Array.from(unitsByFloor.keys()).sort((a, b) => a - b);
    }
    return allTowerFloors.length > 0 ? allTowerFloors : Array.from(unitsByFloor.keys()).sort((a, b) => a - b);
  }, [allTowerFloors, floorFilter, searchQuery, flatTypeFilter, unitsByFloor]);


  const getDepartmentsForFloor = useCallback((floorUnits) => {
    const propPrefix = getPropertyPrefix(selectedProperty?.society_name || "");
    const deptsMap = new Map();

    floorUnits.forEach((u) => {
      let deptName = String(u.unit_type || "").trim();
      const assignments = u.assignments || [];

      const isGeneric =
        !deptName ||
        ["desk", "cabin", "workspace", "workstation", "unit", "flat"].includes(deptName.toLowerCase());

      if (isGeneric) {
        const assignedDept = assignments.find(
          (a) => a.user?.role_details?.department || a.user?.department
        );
        if (assignedDept) {
          deptName = assignedDept.user?.role_details?.department || assignedDept.user?.department;
        } else {
          const parts = String(u.flat_number || "").split("-");
          if (parts.length >= 3 && !/^\d+$/.test(parts[1])) {
            const candidate = parts[1].toLowerCase();
            const matchedMaster = masterDepartmentsList.find(
              (m) =>
                getDepartmentCode(m.name).toLowerCase() === candidate ||
                m.name.toLowerCase() === candidate
            );
            if (matchedMaster) {
              deptName = matchedMaster.name;
            } else if (candidate !== "desk" && candidate !== "cabin") {
              deptName = parts[1].toUpperCase();
            }
          }
        }
      }

      if (!deptName || ["desk", "cabin", "workspace", "workstation", "unit", "flat"].includes(deptName.toLowerCase())) {
        deptName = "General Workspaces";
      }

      if (!deptsMap.has(deptName)) {
        const deptCode = getDepartmentCode(deptName) || getDepartmentShortCode(deptName, deptName);
        deptsMap.set(deptName, {
          deptName,
          deptCode,
          anchorUnits: [],
          desks: [],
          units: [],
          lead: null,
          staffList: [],
          occupiedCount: 0,
          emptyCount: 0,
        });
      }

      const deptObj = deptsMap.get(deptName);
      deptObj.units.push(u);

      const isAnchor = isDepartmentAnchorUnit(u, deptName, deptObj.deptCode, propPrefix);

      if (isAnchor) {
        deptObj.anchorUnits.push(u);
        if (assignments.length > 0 && !deptObj.lead) {
          deptObj.lead = assignments[0].user;
        }
      } else {
        deptObj.desks.push(u);

        if (assignments.length > 0) {
          deptObj.occupiedCount += 1;
          assignments.forEach((a) => {
            if (a.user) {
              if (!deptObj.staffList.some((s) => s.user_id === a.user.user_id)) {
                deptObj.staffList.push(a.user);
              }
              if (
                !deptObj.lead &&
                (a.user?.role_details?.is_lead ||
                  String(a.user?.primary_role || "").includes("Lead") ||
                  String(a.user?.primary_role || "").includes("Head") ||
                  String(a.user?.primary_role || "").includes("Director") ||
                  String(a.user?.primary_role || "").includes("Manager"))
              ) {
                deptObj.lead = a.user;
              }
            }
          });
        } else {
          deptObj.emptyCount += 1;
        }
      }
    });

    for (const deptObj of deptsMap.values()) {
      if (!deptObj.lead && deptObj.staffList.length > 0) {
        deptObj.lead = deptObj.staffList[0];
      }
    }

    return Array.from(deptsMap.values());
  }, [selectedProperty?.society_name, masterDepartmentsList]);

  const displayedFloors = useMemo(() => {
    return floors.slice(floorOffset, floorOffset + floorLimit);
  }, [floors, floorOffset, floorLimit]);

  const selectProperty = (id) => {
    setSearchParams({ propertyId: String(id), tab: "towers" });
  };
  const selectTower = (id) => {
    setSearchParams({ propertyId: String(propertyId), towerId: String(id), tab: "towers" });
  };
  const goBackToProperties = () => setSearchParams({});
  const goBackToTowers = () => setSearchParams({ propertyId: String(propertyId), tab: "towers" });

  const handleSelectTab = useCallback(
    (tabId) => {
      setActiveTab(tabId);
      const nextParams = { propertyId: String(propertyId) };
      if (tabId && tabId !== "towers") {
        nextParams.tab = tabId;
      }
      setSearchParams(nextParams);
    },
    [propertyId, setSearchParams]
  );

  useEffect(() => {
    const loadUnitDetails = async () => {
      if (!unitDetailsModalOpen || !selectedUnitIdForDetails) return;
      setUnitDetailsLoading(true);
      setUnitDetailsError("");
      try {
        const data = await getFlatById(selectedUnitIdForDetails);
        setUnitDetails(data || null);
      } catch (err) {
        setUnitDetailsError(err?.data?.error || err?.message || "Unable to load unit details");
        setUnitDetails(null);
      } finally {
        setUnitDetailsLoading(false);
      }
    };
    loadUnitDetails();
  }, [unitDetailsModalOpen, selectedUnitIdForDetails]);

  const toggleMergeSelection = (unit) => {
    if (!unit || unit.unit_type === "Jodi" || Number(unit.is_merged) === 1 || unit.merged_unit_id) return;
    if (!mergeFloorId) setMergeFloorId(unit.floor_id);
    if (mergeFloorId && mergeFloorId !== unit.floor_id) {
      setMergeFloorId(unit.floor_id);
      setMergeSelected(new Set([unit.flat_id]));
      return;
    }
    setMergeSelected((prev) => {
      const next = new Set(prev);
      next.has(unit.flat_id) ? next.delete(unit.flat_id) : next.add(unit.flat_id);
      return next;
    });
  };

  const doMergeSelected = async () => {
    if (!isSuperAdmin) return;
    const ids = Array.from(mergeSelected);
    if (ids.length < 2) return;
    await mergeUnits({ flat_ids: ids });
    setMergeSelected(new Set());
    setMergeFloorId(null);
    await fetchPropertyDetails();
  };

  const doUnmergeUnit = async (mergedUnitId) => {
    if (!isSuperAdmin || !mergedUnitId) return;
    await unmergeUnits({ merged_unit_id: mergedUnitId });
    await fetchPropertyDetails();
  };

  useEffect(() => {
    if (!isSuperAdmin && mergeMode) {
      setMergeMode(false);
      setMergeSelected(new Set());
      setMergeFloorId(null);
    }
  }, [isSuperAdmin, mergeMode]);


  const departmentDropdownOptions = useMemo(() => {
    const baseList = [
      "HR",
      "Marketing",
      "Development",
      "IT",
      "Management",
      "Operations",
      "Finance",
      "Sales",
      "Engineering",
      "Executive",
      "Legal",
      "Logistics",
      "Administration",
    ];

    const masterNames = masterDepartmentsList
      .map((d) => d.name || d.department_name)
      .filter(Boolean);

    const merged = Array.from(new Set([...baseList, ...masterNames]));
    merged.sort();

    const options = merged.map((name) => ({
      label: name,
      value: name,
    }));

    return options;
  }, [masterDepartmentsList]);

  const openAddDeptModal = (defaultFloor = null) => {
    const targetTower = towerId || (towers.length > 0 ? String(towers[0].tower_id) : "");
    setNewDeptTowerId(targetTower);
    const targetFl = defaultFloor !== null ? String(defaultFloor) : "0";
    setNewDeptFloorNumber(targetFl);
    setNewDeptName("HR");
    setNewDeptDescription("");
    const propPrefix = getPropertyPrefix(selectedProperty?.society_name || "");
    const deptShort = getDepartmentCode("HR");
    setNewDeptCode(propPrefix ? `${propPrefix}-${deptShort}` : deptShort);
    setAddDeptModalOpen(true);
  };

  const availableFloorsForDept = useMemo(() => {
    const targetTId = newDeptTowerId || towerId || (towers[0]?.tower_id ? String(towers[0].tower_id) : "");
    const towerFlats = flats.filter((f) => String(f.tower_id) === String(targetTId));
    const floorSet = new Set(towerFlats.map((f) => Number(f.floor_number)));
    if (!floorSet.has(0)) floorSet.add(0);
    if (!floorSet.has(1)) floorSet.add(1);
    if (!floorSet.has(2)) floorSet.add(2);
    return Array.from(floorSet).sort((a, b) => a - b);
  }, [flats, newDeptTowerId, towerId, towers]);


  // Selected staff object in assign modal
  const selectedStaffObj = useMemo(() => {
    return availableSocietyStaff.find(
      (s) => String(s.user_id || s.id) === String(staffUserIdToAssign)
    );
  }, [availableSocietyStaff, staffUserIdToAssign]);

  // Sync department when staff member is selected
  useEffect(() => {
    if (selectedStaffObj) {
      let dept = "";
      try {
        const rd = typeof selectedStaffObj.role_details === "string"
          ? JSON.parse(selectedStaffObj.role_details)
          : (selectedStaffObj.role_details || {});
        dept = rd.department || selectedStaffObj.department || "";
      } catch (e) {
        dept = selectedStaffObj.department || "";
      }
      setAssignDepartment(dept || unitDetails?.unit_type || "General");
    }
  }, [selectedStaffObj, unitDetails]);

  // Compute newly aligned desk code based on employee/selected department
  const alignedDeskCode = useMemo(() => {
    if (!unitDetails) return "";
    const isCabin =
      String(unitDetails.flat_number || "").includes("-c") ||
      String(unitDetails.flat_number || "").includes("C-") ||
      String(unitDetails.unit_type || "").toLowerCase().includes("cabin");

    const deptToUse = assignDepartment || unitDetails.unit_type || "General";
    return generateWorkspaceCode({
      propertyName: selectedProperty?.society_name || "",
      department: deptToUse,
      type: isCabin ? "cabin" : "desk",
      existingCodes: flats.filter((f) => f.flat_id !== unitDetails.flat_id).map((f) => f.flat_number),
    });
  }, [unitDetails, assignDepartment, selectedProperty, flats]);

  // Load staff list when opening assign modal
  const handleOpenAssignDeskModal = async () => {
    try {
      const staffList = await getSocietyStaff(Number(propertyId));
      setAvailableSocietyStaff(Array.isArray(staffList) ? staffList : []);
    } catch {
      setAvailableSocietyStaff([]);
    }
    setStaffUserIdToAssign("");
    setAssignDepartment(unitDetails?.unit_type || "");
    setAssignDeskModalOpen(true);
  };

  const handleAssignEmployeeToDesk = async (e) => {
    e.preventDefault();
    if (!staffUserIdToAssign || !unitDetails?.flat_id) return;
    setSavingDeskAction(true);
    try {
      const targetDeskCode = alignedDeskCode || unitDetails.flat_number;
      const targetDept = assignDepartment || unitDetails.unit_type;
      await assignStaffToFlat(unitDetails.flat_id, {
        user_id: Number(staffUserIdToAssign),
        desk_code: targetDeskCode,
        department: targetDept,
      });
      toast.success(`Employee assigned & desk code aligned to ${targetDeskCode}`);
      setAssignDeskModalOpen(false);
      setStaffUserIdToAssign("");
      const updated = await getFlatById(unitDetails.flat_id);
      setUnitDetails(updated);
      await fetchPropertyDetails();
    } catch (err) {
      console.error(err);
    } finally {
      setSavingDeskAction(false);
    }
  };

  const handleOpenShiftDeskModal = (assignment) => {
    setStaffToShift(assignment);
    setTargetShiftFlatId("");
    setShiftDeskModalOpen(true);
  };

  const handleShiftEmployeeDesk = async (e) => {
    e.preventDefault();
    if (!targetShiftFlatId || !staffToShift || !unitDetails?.flat_id) return;
    setSavingDeskAction(true);
    try {
      const targetFlat = flats.find((f) => String(f.flat_id) === String(targetShiftFlatId));
      
      let empDept = "";
      try {
        const rd = typeof staffToShift.user?.role_details === "string"
          ? JSON.parse(staffToShift.user.role_details)
          : (staffToShift.user?.role_details || {});
        empDept = rd.department || targetFlat?.unit_type || "";
      } catch (e) {
        empDept = targetFlat?.unit_type || "";
      }

      const isCabin =
        String(targetFlat?.flat_number || "").includes("-c") ||
        String(targetFlat?.flat_number || "").includes("C-") ||
        String(targetFlat?.unit_type || "").toLowerCase().includes("cabin");

      const newAlignedCode = generateWorkspaceCode({
        propertyName: selectedProperty?.society_name || "",
        department: empDept || targetFlat?.unit_type || "General",
        type: isCabin ? "cabin" : "desk",
        existingCodes: flats.filter((f) => f.flat_id !== targetFlat?.flat_id).map((f) => f.flat_number),
      });

      await unassignStaffFromFlat(unitDetails.flat_id, staffToShift.user_id);
      await assignStaffToFlat(targetShiftFlatId, {
        user_id: staffToShift.user_id,
        desk_code: newAlignedCode,
        department: empDept || targetFlat?.unit_type || null,
      });
      toast.success(`Employee shifted & desk code aligned to ${newAlignedCode}`);
      setShiftDeskModalOpen(false);
      setStaffToShift(null);
      setTargetShiftFlatId("");
      const updated = await getFlatById(unitDetails.flat_id);
      setUnitDetails(updated);
      await fetchPropertyDetails();
    } catch (err) {
      console.error(err);
    } finally {
      setSavingDeskAction(false);
    }
  };

  const handleUnassignEmployeeDesk = async (userId) => {
    if (!window.confirm("Are you sure you want to unassign this employee? This will empty the desk and clear their desk code.")) return;
    try {
      await unassignStaffFromFlat(unitDetails.flat_id, userId);
      const updated = await getFlatById(unitDetails.flat_id);
      setUnitDetails(updated);
      await fetchPropertyDetails();
    } catch (err) {
      console.error(err);
    }
  };

  const handleRemoveDepartmentFromFloor = async (floorNumber, dept) => {
    if (!dept) return;
    const flName = formatFloorName(floorNumber);
    
    if (dept.occupiedCount > 0) {
      const confirmOccupied = window.confirm(
        `"${dept.deptName} Department" on ${flName} has ${dept.occupiedCount} assigned staff member(s).\n\nAre you sure you want to unassign all staff and remove this department and its ${dept.desks.length} workspace(s) from this floor?`
      );
      if (!confirmOccupied) return;
    } else {
      const confirmRemove = window.confirm(
        `Are you sure you want to remove "${dept.deptName} Department" from ${flName}? This will remove all ${dept.desks.length} workspace(s) in this department on this floor.`
      );
      if (!confirmRemove) return;
    }

    try {
      const unitsToRemove = [
        ...(dept.anchorUnits || []).map((u) => ({ action: "REMOVE", flat_id: u.flat_id })),
        ...(dept.desks || []).map((u) => ({ action: "REMOVE", flat_id: u.flat_id })),
        ...(dept.units || []).map((u) => ({ action: "REMOVE", flat_id: u.flat_id })),
      ].filter((u, idx, arr) => u.flat_id && arr.findIndex((x) => x.flat_id === u.flat_id) === idx);

      if (unitsToRemove.length === 0) {
        toast.info("No units found to remove for this department.");
        return;
      }

      await updateFlatStructure(unitsToRemove);
      toast.success(`"${dept.deptName} Department" removed from ${flName}`);

      setSelectedDeptOnFloor((prev) => ({
        ...prev,
        [floorNumber]: null,
      }));

      await fetchPropertyDetails();
    } catch (err) {
      console.error("Error removing department from floor:", err);
      toast.error(err?.response?.data?.message || "Failed to remove department");
    }
  };

  const handleRemoveDesk = async (unit) => {
    if (!unit?.flat_id) return;
    if (unit.assignments && unit.assignments.length > 0) {
      toast.error("Please unassign the employee from this workspace before removing it.");
      return;
    }
    if (!window.confirm(`Are you sure you want to remove workspace "${unit.flat_number}"?`)) {
      return;
    }
    try {
      await updateFlatStructure([{ action: "REMOVE", flat_id: unit.flat_id }]);
      toast.success(`Workspace "${unit.flat_number}" removed successfully`);
      await fetchPropertyDetails();
    } catch (err) {
      console.error("Error removing workspace:", err);
      toast.error(err?.response?.data?.message || "Failed to remove workspace");
    }
  };

  const handleAddPropertyDepartment = async (e) => {
    e.preventDefault();
    if (!newDeptName.trim()) {
      toast.error("Department name is required.");
      return;
    }
    const cleanName = newDeptName.trim();
    const targetTowerId = newDeptTowerId || towerId || (towers.length > 0 ? towers[0].tower_id : null);
    if (!targetTowerId) {
      toast.error("Please configure at least one building / wing for this property first.");
      return;
    }

    setSavingDept(true);
    try {
      const flNum = Number(newDeptFloorNumber || 0);
      const existingOnFloor = flats.filter(
        (f) => String(f.tower_id) === String(targetTowerId) && Number(f.floor_number) === flNum
      );
      const nextIdx = existingOnFloor.length + 1;
      const finalCode =
        newDeptCode.trim() ||
        (flNum === 0
          ? `G${String(nextIdx).padStart(2, "0")}`
          : flNum < 0
            ? `B${Math.abs(flNum)}${String(nextIdx).padStart(2, "0")}`
            : `${flNum}${String(nextIdx).padStart(2, "0")}`);

      // 1. Add department unit directly to building floor
      await updateFlatStructure([
        {
          action: "ADD",
          tower_id: Number(targetTowerId),
          floor_number: flNum,
          flat_number: finalCode,
          unit_type: cleanName,
        },
      ]);

      // 2. Also register in master department taxonomy
      await masterService.createMasterDepartmentType({
        name: cleanName,
        property_type_code: propertyCategory,
        property_id: Number(propertyId),
        description: newDeptDescription.trim() || null,
        is_active: 1,
      });

      const towerObj = towers.find((t) => String(t.tower_id) === String(targetTowerId));
      toast.success(
        `Department "${cleanName}" (${finalCode}) added to ${towerObj?.tower_name || "Building"} - ${formatFloorName(flNum)}!`
      );

      setNewDeptName("");
      setNewDeptDescription("");
      setNewDeptCode("");
      setAddDeptModalOpen(false);
      await fetchPropertyDetails();
    } catch (err) {
      console.error("Error adding property department:", err);
      toast.error("Failed to add department to property.");
    } finally {
      setSavingDept(false);
    }
  };

  const selectedMergeUnits = useMemo(() => {
    if (!mergeSelected.size) return [];
    const selected = flats.filter((f) => mergeSelected.has(f.flat_id));
    selected.sort((a, b) => String(a.flat_number || "").localeCompare(String(b.flat_number || ""), undefined, { numeric: true }));
    return selected;
  }, [flats, mergeSelected]);

  const propertyContext = useMemo(() => {
    if (viewLevel < 2 || !selectedProperty) return null;
    return {
      property: selectedProperty,
      activeTab,
      tabs: propertyTabs,
      onSelectTab: handleSelectTab,
      onBack: goBackToProperties,
    };
  }, [viewLevel, selectedProperty, activeTab, propertyTabs, handleSelectTab, goBackToProperties]);

  return (
    <AdminLayout title="Property Management" propertyContext={propertyContext}>
      <div className={styles.page}>
        <div className={styles.actions} style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginBottom: '16px' }}>
          {viewLevel === 1 && isSuperAdmin && (
            <Button icon={<Plus size={16} />} onClick={() => { setPropertyEditing(null); setPropertyModalOpen(true); }}>
              Add Property
            </Button>
          )}
          {viewLevel === 2 && isSuperAdmin && (
            <Button variant="outline" icon={<Link2 size={16} />} onClick={() => setInviteModalOpen(true)}>
              Invite User / Staff
            </Button>
          )}
          {viewLevel === 2 && activeTab === "towers" && isSuperAdmin && (
            <>
              <Button variant="outline" icon={<Plus size={15} />} onClick={() => openAddDeptModal()}>
                Add Department
              </Button>
              <Button icon={<Building size={16} />} onClick={() => setTowerModalOpen(true)}>
                {isIndustrial ? "Manage Buildings" : (isCommercial ? "Manage Wings" : "Manage Towers")}
              </Button>
            </>
          )}
          {viewLevel === 3 && isSuperAdmin && (
            <>
              <Button variant="outline" icon={<Plus size={15} />} onClick={() => openAddDeptModal()}>
                Add Department
              </Button>
              <Button
                variant={mergeMode ? "primary" : "outline"}
                onClick={() => {
                  setMergeMode(!mergeMode);
                  setMergeSelected(new Set());
                  setMergeFloorId(null);
                }}
              >
                {mergeMode
                  ? "Cancel"
                  : isIndustrial
                    ? "Combine Desks / Spaces"
                    : isCommercial
                      ? "Combine Store Spaces"
                      : "Merge Units"}
              </Button>
              <Button variant="outline" icon={<Upload size={15} />} onClick={() => setImportModalOpen(true)}>
                Import {isIndustrial ? "Desks & Cabins" : isCommercial ? "Commercial Units" : "Units"}
              </Button>
              <Button icon={<Plus size={15} />} onClick={() => setUnitModalOpen(true)}>
                Generate / Update {isIndustrial ? "Desks & Cabins" : isCommercial ? "Commercial Units" : "Units"}
              </Button>
            </>
          )}
        </div>

        {viewLevel === 3 && mergeSelected.size > 0 && (
          <div className={styles.mergeBar}>
            <div className={styles.mergeInfo}>
              <div>Selected to {isIndustrial ? "combine" : "merge"}: <strong>{mergeSelected.size}</strong></div>
              <div className={styles.mergeChips}>
                {selectedMergeUnits.map((u) => (
                  <span key={u.flat_id} className={styles.mergeChip}>{u.flat_number}</span>
                ))}
              </div>
            </div>
            <Button onClick={doMergeSelected}>
              {isIndustrial ? "Combine Selected" : isCommercial ? "Combine Units" : "Merge"}
            </Button>
          </div>
        )}

        {/* Dedicated View when user selects any property module subpage */}
        {viewLevel === 2 && activeTab !== "towers" ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>

            {activeTab === "documents" && (
              <PropertyDocuments
                societyId={Number(propertyId)}
                propertyType={selectedProperty?.society_type}
              />
            )}
            {activeTab === "approvals" && (
              <PropertyApprovals
                societyId={Number(propertyId)}
                propertyType={selectedProperty?.society_type}
                onRequestsUpdated={setPendingApprovalsCount}
              />
            )}
            {activeTab === "directories" && (
              <PropertyDirectories
                societyId={Number(propertyId)}
                propertyType={selectedProperty?.society_type}
              />
            )}
            {activeTab === "gates" && (
              <PropertyGates
                societyId={Number(propertyId)}
                propertyType={selectedProperty?.society_type}
                propertyName={selectedProperty?.society_name || selectedProperty?.name}
              />
            )}
            {activeTab === "staff" && (
              <PropertyStaff
                societyId={Number(propertyId)}
                propertyType={selectedProperty?.society_type}
              />
            )}
            {(activeTab === "committee" || activeTab === "leadership") && (
              <PropertyCommittee
                societyId={Number(propertyId)}
                propertyType={selectedProperty?.society_type}
              />
            )}
            {activeTab === "settings" && (
              <PropertySettings
                societyId={Number(propertyId)}
                propertyType={selectedProperty?.society_type}
              />
            )}
            {activeTab === "network" && (
              <PropertyDeliveryCabNetwork
                societyId={Number(propertyId)}
              />
            )}
            {activeTab === "visits" && (
              <PropertyVisitorPasses
                societyId={Number(propertyId)}
                propertyName={selectedProperty?.society_name || selectedProperty?.name}
              />
            )}
            {activeTab === "industrial-settings" && (
              <PropertyIndustrialSettings
                societyId={Number(propertyId)}
              />
            )}
            {activeTab === "testers" && (
              <PropertyTesters
                societyId={Number(propertyId)}
                propertyType={selectedProperty?.society_type}
                propertyName={selectedProperty?.society_name}
              />
            )}
            {activeTab === "chats" && (
              <CommunityMessages
                societyId={Number(propertyId)}
                embedded={true}
              />
            )}
            {activeTab === "support" && (
              <Support
                societyId={Number(propertyId)}
                embedded={true}
              />
            )}
          </div>
        ) : (
          <div className={styles.card}>
            <div className={styles.cardHeader}>
              <div className={styles.headerTop}>
                <h2 className={styles.title} style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  {viewLevel === 2 && (
                    <Button variant="outline" onClick={goBackToProperties} size="sm" title="Back to Properties" style={{ padding: '6px' }}>
                      <ChevronLeft size={16} />
                    </Button>
                  )}
                  {viewLevel === 3 && (
                    <Button variant="outline" onClick={goBackToTowers} size="sm" title="Back to Towers" style={{ padding: '6px' }}>
                      <ChevronLeft size={16} />
                    </Button>
                  )}
                  <span>
                    {viewLevel === 1 && "Properties"}
                    {viewLevel === 2 && `${isIndustrial ? "Buildings & Departments" : (isCommercial ? "Wings & Units" : "Towers")} in ${selectedProperty?.society_name || 'Property'}`}
                    {viewLevel === 3 && `${isIndustrial ? "Departments & Desks" : "Units"} in ${selectedTower?.tower_name || (isIndustrial ? 'Building' : 'Tower')} (${selectedProperty?.society_name || 'Property'})`}
                  </span>
                </h2>
              </div>

              <div className={styles.filtersGrid}>
                {viewLevel === 1 && (
                  <div style={{ minWidth: "160px" }}>
                    <SelectDropdown
                      name="propertyTypeFilter"
                      value={propertyTypeFilter}
                      onChange={(e) => setPropertyTypeFilter(e?.target ? e.target.value : (e?.value ?? e ?? ""))}
                      options={[
                        { label: "All Types", value: "" },
                        ...availablePropertyTypes.map((t) => ({ label: t, value: t })),
                      ]}
                      placeholder="All Types"
                    />
                  </div>
                )}
                {viewLevel === 3 && (
                  <>
                    <div style={{ minWidth: "160px" }}>
                      <SelectDropdown
                        name="flatTypeFilter"
                        value={flatTypeFilter}
                        onChange={(e) => setFlatTypeFilter(e?.target ? e.target.value : (e?.value ?? e ?? ""))}
                        options={[
                          {
                            label: isIndustrial
                              ? "All Departments"
                              : isCommercial
                                ? "All Commercial Units"
                                : "All Types",
                            value: "",
                          },
                          ...availableFlatTypes.map((t) => ({ label: t, value: t })),
                        ]}
                        placeholder={isIndustrial ? "All Departments" : "All Types"}
                      />
                    </div>
                    <div style={{ minWidth: "140px" }}>
                      <SelectDropdown
                        name="floorFilter"
                        value={floorFilter}
                        onChange={(e) => setFloorFilter(e?.target ? e.target.value : (e?.value ?? e ?? ""))}
                        options={[
                          { label: "All Floors", value: "" },
                          ...availableFloors.map((fl) => ({ label: `Floor ${fl}`, value: String(fl) })),
                        ]}
                        placeholder="All Floors"
                      />
                    </div>
                  </>
                )}
                <div style={{ flex: 1, minWidth: "220px" }}>
                  <SearchInput
                    value={searchQuery}
                    onChange={(val) => setSearchQuery(val)}
                    placeholder={
                      viewLevel === 1
                        ? "Search properties..."
                        : viewLevel === 2
                          ? isIndustrial
                            ? "Search buildings & departments..."
                            : isCommercial
                              ? "Search wings & floors..."
                              : "Search towers..."
                          : isIndustrial
                            ? "Search desk / cabin / floor..."
                            : isCommercial
                              ? "Search shop / store / floor..."
                              : "Search unit / floor..."
                    }
                  />
                </div>
              </div>
            </div>

            {viewLevel === 1 && (
              loadingProperties ? (
                <div className={styles.slotGrid}>
                  {Array.from({ length: 8 }).map((_, idx) => (
                    <div key={idx} className={styles.slotCardSkeleton}>
                      <div className={styles.slotTop}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', width: '100%' }}>
                          <div className={styles.slotSkeletonShimmer} style={{ width: '48px', height: '48px', borderRadius: '8px', flexShrink: 0 }} />
                          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            <div className={styles.slotSkeletonShimmer} style={{ width: '65%', height: '18px' }} />
                            <div className={styles.slotSkeletonShimmer} style={{ width: '45%', height: '12px' }} />
                          </div>
                        </div>
                      </div>
                      <div className={styles.slotBottom}>
                        <div className={styles.slotSkeletonShimmer} style={{ width: '100%', height: '36px', borderRadius: '8px' }} />
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <>
                  <div className={styles.slotGrid}>
                    {displayedProperties.map((p) => (
                      <div key={p.society_id} className={styles.slotCard} onClick={() => selectProperty(p.society_id)}>
                        <div className={styles.slotTop}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            {p.icon_url ? (
                              <img src={resolveBackendUrl(p.icon_url)} alt="" className={styles.propertyIcon} onError={(e) => { e.currentTarget.style.display = "none"; }} />
                            ) : (
                              <div className={styles.propertyIcon} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f8fafc' }}>
                                <Building2 className={styles.propertyIconFallback} size={24} />
                              </div>
                            )}
                            <div>
                              <div className={styles.slotCode}>{p.society_name}</div>
                              <div className={styles.slotMeta} style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap', marginTop: '2px' }}>
                                <span>{p.society_type || 'Property'} • ID: {p.society_id}</span>
                                {p.parent_society_id ? (
                                  <span style={{ color: '#6366f1', fontWeight: 600, fontSize: '11px', background: 'rgba(99, 102, 241, 0.1)', padding: '1px 6px', borderRadius: '4px' }}>
                                    ↳ Child of #{p.parent_society_id}
                                  </span>
                                ) : (
                                  <span style={{ color: '#059669', fontWeight: 600, fontSize: '11px', background: 'rgba(16, 185, 129, 0.1)', padding: '1px 6px', borderRadius: '4px' }}>
                                    Root
                                  </span>
                                )}
                                {p.property_status && p.property_status !== 'operational' && (
                                  <span style={{
                                    fontSize: '11px',
                                    padding: '1px 6px',
                                    borderRadius: '4px',
                                    background: p.property_status === 'under_construction' ? '#fef3c7' : '#f1f5f9',
                                    color: p.property_status === 'under_construction' ? '#d97706' : '#64748b',
                                    fontWeight: 600,
                                    daylight: 'none'
                                  }}>
                                    {p.property_status === 'under_construction' ? 'Under Construction' : p.property_status}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                        {isSuperAdmin && (
                          <div className={styles.slotBottom}>
                            <Button
                              variant="primary"
                              onClick={(e) => { e.stopPropagation(); setPropertyEditing(p); setPropertyModalOpen(true); }}
                              style={{ width: '100%' }}
                            >
                              Edit Property
                            </Button>
                          </div>
                        )}
                      </div>
                    ))}
                    {!displayedProperties.length && (
                      <div className={styles.empty}>No properties found.</div>
                    )}
                  </div>
                  {filteredProperties.length > 0 && (
                    <div className={styles.metaRowBottom}>
                      <div className={styles.metaText}>
                        Showing <span className={styles.metaTextBold}>{propOffset + 1}-{Math.min(propOffset + propLimit, filteredProperties.length)}</span> of <span className={styles.metaTextBold}>{filteredProperties.length}</span>
                      </div>
                      <div className={styles.pager}>
                        <div className={styles.pagerSelectWrapper} style={{ minWidth: '120px' }}>
                          <SelectDropdown
                            name="propLimit"
                            value={propLimit}
                            onChange={(e) => setPropLimit(Number(e?.target ? e.target.value : (e?.value ?? e ?? 25)))}
                            options={[
                              { label: "25 / page", value: 25 },
                              { label: "50 / page", value: 50 },
                              { label: "100 / page", value: 100 }
                            ]}
                          />
                        </div>
                        <div className={styles.metaText}>
                          Page <span className={styles.metaTextBold}>{Math.floor(propOffset / propLimit) + 1}</span> of <span className={styles.metaTextBold}>{Math.ceil(filteredProperties.length / propLimit)}</span>
                        </div>
                        <div className={styles.pagerButtons}>
                          <Button variant="outline" size="sm" disabled={propOffset === 0} onClick={() => setPropOffset((p) => Math.max(0, p - propLimit))}>Prev</Button>
                          <Button variant="outline" size="sm" disabled={propOffset + propLimit >= filteredProperties.length} onClick={() => setPropOffset((p) => p + propLimit)}>Next</Button>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )
            )}

            {viewLevel === 2 && (
              loadingDetails ? (
                <div style={{ padding: '16px' }}>
                  <div className={styles.slotGrid}>
                    {Array.from({ length: 6 }).map((_, idx) => (
                      <div key={idx} className={styles.slotCardSkeleton}>
                        <div className={styles.slotTop}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', width: '100%' }}>
                            <div className={styles.slotSkeletonShimmer} style={{ width: '48px', height: '48px', borderRadius: '8px', flexShrink: 0 }} />
                            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '8px' }}>
                              <div className={styles.slotSkeletonShimmer} style={{ width: '50%', height: '18px' }} />
                              <div className={styles.slotSkeletonShimmer} style={{ width: '30%', height: '12px' }} />
                            </div>
                          </div>
                        </div>
                        <div className={styles.slotBottom}>
                          <div className={styles.slotSkeletonShimmer} style={{ width: '100%', height: '36px', borderRadius: '8px' }} />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div style={{ padding: '16px' }}>

                  {activeTab === "towers" && (
                    <>
                      <div className={styles.slotGrid}>
                        {displayedTowers.map((t) => (
                          <div key={t.tower_id} className={styles.slotCard} onClick={() => selectTower(t.tower_id)}>
                            <div className={styles.slotTop}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                <div className={styles.propertyIcon} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f8fafc' }}>
                                  <Building2 className={styles.propertyIconFallback} size={24} />
                                </div>
                                <div>
                                  <div className={styles.slotCode}>{t.tower_name}</div>
                                  <div className={styles.slotMeta}>ID: {t.tower_id}</div>
                                </div>
                              </div>
                            </div>
                            <div className={styles.slotBottom}>
                              <Button variant="primary" style={{ width: '100%' }}>
                                {isIndustrial
                                  ? "View Departments & Desks"
                                  : isCommercial
                                    ? "View Shops & Floors"
                                    : "View Units"}
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                      {!displayedTowers.length && (
                        <div className={styles.empty}>
                          {isIndustrial
                            ? "No buildings or departments found."
                            : isCommercial
                              ? "No commercial wings found."
                              : "No towers found."}
                        </div>
                      )}

                      {filteredTowers.length > 0 && (
                        <div className={styles.metaRowBottom}>
                          <div className={styles.metaText}>
                            Showing <span className={styles.metaTextBold}>{towerOffset + 1}-{Math.min(towerOffset + towerLimit, filteredTowers.length)}</span> of <span className={styles.metaTextBold}>{filteredTowers.length}</span>
                          </div>
                          <div className={styles.pager}>
                            <div className={styles.pagerSelectWrapper} style={{ minWidth: '120px' }}>
                              <SelectDropdown
                                name="towerLimit"
                                value={towerLimit}
                                onChange={(e) => setTowerLimit(Number(e?.target ? e.target.value : (e?.value ?? e ?? 25)))}
                                options={[
                                  { label: "25 / page", value: 25 },
                                  { label: "50 / page", value: 50 },
                                  { label: "100 / page", value: 100 }
                                ]}
                              />
                            </div>
                            <div className={styles.metaText}>
                              Page <span className={styles.metaTextBold}>{Math.floor(towerOffset / towerLimit) + 1}</span> of <span className={styles.metaTextBold}>{Math.ceil(filteredTowers.length / towerLimit)}</span>
                            </div>
                            <div className={styles.pagerButtons}>
                              <Button variant="outline" size="sm" disabled={towerOffset === 0} onClick={() => setTowerOffset((p) => Math.max(0, p - towerLimit))}>Prev</Button>
                              <Button variant="outline" size="sm" disabled={towerOffset + towerLimit >= filteredTowers.length} onClick={() => setTowerOffset((p) => p + towerLimit)}>Next</Button>
                            </div>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )
            )}

            {viewLevel === 3 && (
              loadingDetails ? (
                <div className={styles.units}>
                  {Array.from({ length: 3 }).map((_, fIdx) => (
                    <div key={fIdx} className={styles.floorCard}>
                      <div className={styles.floorHeader} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div className={styles.slotSkeletonShimmer} style={{ width: '90px', height: '16px', borderRadius: '4px' }} />
                        {isIndustrial && (
                          <div style={{ display: 'flex', gap: '8px' }}>
                            <div className={styles.slotSkeletonShimmer} style={{ width: '95px', height: '24px', borderRadius: '6px' }} />
                            <div className={styles.slotSkeletonShimmer} style={{ width: '115px', height: '24px', borderRadius: '6px' }} />
                          </div>
                        )}
                      </div>

                      {isIndustrial ? (
                        <div className={styles.departmentGrid}>
                          {Array.from({ length: 4 }).map((_, dIdx) => (
                            <div key={dIdx} className={styles.departmentCard} style={{ pointerEvents: 'none' }}>
                              <div className={styles.deptCardHeader}>
                                <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start', flex: 1 }}>
                                  <div className={styles.slotSkeletonShimmer} style={{ width: '38px', height: '38px', borderRadius: '10px', flexShrink: 0 }} />
                                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                    <div className={styles.slotSkeletonShimmer} style={{ width: '60%', height: '16px' }} />
                                    <div className={styles.slotSkeletonShimmer} style={{ width: '40%', height: '12px' }} />
                                  </div>
                                </div>
                              </div>
                              <div style={{ marginTop: '12px' }}>
                                <div className={styles.slotSkeletonShimmer} style={{ width: '100%', height: '36px', borderRadius: '8px' }} />
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : isCommercial ? (
                        <div className={styles.commercialGrid}>
                          {Array.from({ length: 4 }).map((_, cIdx) => (
                            <div key={cIdx} className={styles.commercialCard} style={{ pointerEvents: 'none' }}>
                              <div className={styles.commCardHeader}>
                                <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
                                  <div className={styles.slotSkeletonShimmer} style={{ width: '40px', height: '40px', borderRadius: '10px', flexShrink: 0 }} />
                                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                    <div className={styles.slotSkeletonShimmer} style={{ width: '80px', height: '16px' }} />
                                    <div className={styles.slotSkeletonShimmer} style={{ width: '60px', height: '12px' }} />
                                  </div>
                                </div>
                              </div>
                              <div style={{ marginTop: '10px' }}>
                                <div className={styles.slotSkeletonShimmer} style={{ width: '100%', height: '28px', borderRadius: '6px' }} />
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className={styles.unitGrid}>
                          {Array.from({ length: 8 }).map((_, uIdx) => (
                            <div
                              key={uIdx}
                              className={styles.unit}
                              style={{ pointerEvents: 'none', minHeight: '62px' }}
                            >
                              <div className={styles.slotSkeletonShimmer} style={{ width: '38px', height: '14px', borderRadius: '4px' }} />
                              <div className={styles.slotSkeletonShimmer} style={{ width: '58px', height: '18px', borderRadius: '999px', marginTop: '8px' }} />
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className={styles.units}>
                  {displayedFloors.map((floor) => {
                    const floorUnits = unitsByFloor.get(floor) || [];

                    return (
                      <div key={floor} className={styles.floorCard}>
                        <div className={styles.floorHeader} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{ fontWeight: 700 }}>{formatFloorName(floor)}</span>
                          {isSuperAdmin && isIndustrial && (
                            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  setUnitModalInitialFloor(floor);
                                  setUnitModalInitialDept("");
                                  setUnitModalOpen(true);
                                }}
                                style={{ fontSize: "11.5px", padding: "3px 10px", display: "inline-flex", alignItems: "center", gap: "4px" }}
                              >
                                <Plus size={12} /> Workspaces
                              </Button>
                              <Button
                                type="button"
                                variant="secondary"
                                size="sm"
                                onClick={() => openAddDeptModal(floor)}
                                style={{ fontSize: "11.5px", padding: "3px 10px", display: "inline-flex", alignItems: "center", gap: "4px" }}
                              >
                                <Plus size={12} /> Add Department
                              </Button>
                            </div>
                          )}
                        </div>

                        {/* 1. INDUSTRIAL: Department Floor-wise View with Team Tree & Desk Codes */}
                        {isIndustrial ? (
                          (() => {
                            const activeDeptName = selectedDeptOnFloor[floor];
                            const floorDepts = getDepartmentsForFloor(floorUnits);
                            const activeDept = activeDeptName
                              ? floorDepts.find((d) => d.deptName === activeDeptName)
                              : null;

                            if (activeDept) {
                              // 1B. DRILLDOWN: SHOW "THIS LIST" OF DESKS BELONGING TO THIS DEPARTMENT!
                              return (
                                <div className={styles.deptDetailContainer}>
                                  <div className={styles.deptDetailHeader}>
                                    <div className={styles.deptDetailLeft}>
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        title="Back to Departments"
                                        onClick={() =>
                                          setSelectedDeptOnFloor((prev) => ({
                                            ...prev,
                                            [floor]: null,
                                          }))
                                        }
                                        style={{
                                          padding: "6px",
                                          borderRadius: "8px",
                                          display: "inline-flex",
                                          alignItems: "center",
                                          justifyContent: "center",
                                        }}
                                      >
                                        <ChevronLeft size={16} />
                                      </Button>
                                      <div className={styles.deptIconWrap}>
                                        {activeDept.deptCode.toUpperCase()}
                                      </div>
                                      <div>
                                        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                          <h3 className={styles.deptDetailTitle}>
                                            {activeDept.deptName.replace(/\s*department$/i, "")} Department
                                          </h3>
                                        </div>
                                        <div className={styles.deptDetailSubtitle}>
                                          <span
                                            style={{
                                              display: "inline-block",
                                              width: "7px",
                                              height: "7px",
                                              borderRadius: "50%",
                                              background: activeDept.desks.length === 0 ? "#94a3b8" : activeDept.occupiedCount > 0 ? "#3b82f6" : "#22c55e",
                                              marginRight: "6px",
                                            }}
                                          />
                                          <span>{formatFloorName(floor)}</span>
                                          <span style={{ color: "#cbd5e1", margin: "0 5px" }}>•</span>
                                          <span>
                                            {activeDept.desks.length} {activeDept.desks.length === 1 ? "Workspace" : "Workspaces"}
                                          </span>
                                          <span style={{ color: "#cbd5e1", margin: "0 5px" }}>•</span>
                                          <span>
                                            {activeDept.occupiedCount > 0
                                              ? `${activeDept.occupiedCount} Occupied, ${activeDept.emptyCount} Available`
                                              : "All Available"}
                                          </span>
                                          {activeDept.lead && (
                                            <>
                                              <span style={{ color: "#cbd5e1", margin: "0 5px" }}>•</span>
                                              <span>Lead: {activeDept.lead.full_name}</span>
                                            </>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                    <div style={{ display: "flex", gap: "8px", alignItems: "center", flexWrap: "wrap" }}>
                                      <Button
                                        variant="primary"
                                        size="sm"
                                        onClick={() => {
                                          setUnitModalInitialFloor(floor);
                                          setUnitModalInitialDept(activeDept.deptName);
                                          setUnitModalOpen(true);
                                        }}
                                        style={{ fontSize: "12.5px", padding: "7px 14px", display: "inline-flex", alignItems: "center", gap: "6px", borderRadius: "8px", fontWeight: 600 }}
                                      >
                                        <Plus size={14} /> Add Desks to {activeDept.deptName}
                                      </Button>
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => {
                                          const targetId = activeDept.anchorUnits[0]?.flat_id || activeDept.desks[0]?.flat_id || activeDept.units[0]?.flat_id;
                                          if (targetId) {
                                            navigate(
                                              `/properties/department/${targetId}?propertyId=${propertyId}&towerId=${towerId}&dept=${encodeURIComponent(activeDept.deptName)}`
                                            );
                                          }
                                        }}
                                        style={{ fontSize: "12.5px", padding: "7px 14px", borderRadius: "8px", fontWeight: 600 }}
                                      >
                                        View Team Tree
                                      </Button>
                                      {isSuperAdmin && (
                                        <Button
                                          variant="outline"
                                          size="sm"
                                          onClick={() => handleRemoveDepartmentFromFloor(floor, activeDept)}
                                          title="Remove department & its desks from this floor"
                                          style={{
                                            fontSize: "12.5px",
                                            padding: "7px 12px",
                                            color: "#dc2626",
                                            borderColor: "#fecaca",
                                            borderRadius: "8px",
                                            fontWeight: 600,
                                            display: "inline-flex",
                                            alignItems: "center",
                                            gap: "5px",
                                            background: "#fff",
                                          }}
                                        >
                                          <Trash2 size={13} /> Remove Department
                                        </Button>
                                      )}
                                    </div>
                                  </div>

                                  {activeDept.desks.length === 0 ? (
                                    <div className={styles.emptyDeptFloor} style={{ margin: "16px 0", padding: "32px 20px" }}>
                                      <p style={{ margin: 0, fontSize: "14px", color: "#64748b", fontWeight: 500 }}>
                                        No desks or cabins added to {activeDept.deptName} Department on this floor yet.
                                      </p>
                                      <div style={{ display: "flex", gap: "10px", marginTop: "14px" }}>
                                        <Button
                                          variant="primary"
                                          size="sm"
                                          onClick={() => {
                                            setUnitModalInitialFloor(floor);
                                            setUnitModalInitialDept(activeDept.deptName);
                                            setUnitModalOpen(true);
                                          }}
                                          style={{ fontSize: "12px", padding: "6px 14px", display: "inline-flex", alignItems: "center", gap: "6px" }}
                                        >
                                          <Plus size={14} /> Add Desks to {activeDept.deptName}
                                        </Button>
                                      </div>
                                    </div>
                                  ) : (
                                    <div className={styles.deskGrid}>
                                      {activeDept.desks.map((u) => {
                                      const isOccupied = (u.assignments || []).length > 0;
                                      const assignedUser = u.assignments?.[0]?.user;
                                      const isCabin =
                                        String(u.flat_number || "").includes("-c") ||
                                        String(u.flat_number || "").includes("C-") ||
                                        String(u.unit_type || "").toLowerCase().includes("cabin");

                                      return (
                                        <div
                                          key={u.flat_id}
                                          className={[
                                            styles.deskCard,
                                            isOccupied ? styles.deskCardOccupied : styles.deskCardEmpty,
                                          ].join(" ")}
                                          onClick={() => {
                                            setSelectedUnitIdForDetails(u.flat_id);
                                            setUnitDetailsModalOpen(true);
                                          }}
                                        >
                                          <div className={styles.deskCardTop}>
                                            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                                              <div className={styles.deskIconWrap}>
                                                {isCabin ? <DoorOpen size={18} /> : <Laptop size={18} />}
                                              </div>
                                              <div>
                                                <div className={styles.deskCodeTitle}>{u.flat_number}</div>
                                                <div className={styles.deskTypeSub}>
                                                  {isCabin ? "Private Cabin" : "Workstation / Desk"}
                                                </div>
                                              </div>
                                            </div>
                                            <span className={isOccupied ? styles.occupiedPill : styles.availablePill}>
                                              {isOccupied ? "Occupied" : "🟢 Available"}
                                            </span>
                                          </div>

                                          <div className={styles.deskOccupantSection}>
                                            {isOccupied ? (
                                              <div className={styles.occupantInfo}>
                                                <div className={styles.occupantAvatar}>
                                                  {assignedUser?.full_name?.charAt(0) || "U"}
                                                </div>
                                                <div className={styles.occupantMeta}>
                                                  <div className={styles.occupantName}>
                                                    {assignedUser?.full_name || "Assigned Staff"}
                                                  </div>
                                                  <div className={styles.occupantRole}>
                                                    {assignedUser?.role_details?.designation ||
                                                      assignedUser?.system_role ||
                                                      assignedUser?.primary_role ||
                                                      "Employee"}
                                                  </div>
                                                </div>
                                              </div>
                                            ) : (
                                              <div className={styles.emptyOccupantInfo}>
                                                <span className={styles.emptyText}>Empty workstation ready for assignment</span>
                                              </div>
                                            )}
                                          </div>

                                          <div
                                            className={styles.deskCardActions}
                                            onClick={(e) => e.stopPropagation()}
                                          >
                                            {isOccupied ? (
                                              <>
                                                <Button
                                                  variant="outline"
                                                  size="sm"
                                                  onClick={() => {
                                                    setSelectedUnitIdForDetails(u.flat_id);
                                                    setUnitDetails(u);
                                                    handleOpenShiftDeskModal(u.assignments[0]);
                                                  }}
                                                  style={{ fontSize: "11px", padding: "4px 8px" }}
                                                >
                                                  Shift Desk
                                                </Button>
                                                <Button
                                                  variant="outline"
                                                  size="sm"
                                                  onClick={() => {
                                                    setSelectedUnitIdForDetails(u.flat_id);
                                                    setUnitDetails(u);
                                                    handleUnassignEmployeeDesk(assignedUser.user_id || assignedUser.id);
                                                  }}
                                                  style={{
                                                    fontSize: "11px",
                                                    padding: "4px 8px",
                                                    color: "#dc2626",
                                                    borderColor: "#fecaca",
                                                  }}
                                                >
                                                  Unassign
                                                </Button>
                                              </>
                                            ) : (
                                              <div style={{ display: "flex", gap: "6px", width: "100%" }}>
                                                <Button
                                                  variant="primary"
                                                  size="sm"
                                                  onClick={() => {
                                                    setSelectedUnitIdForDetails(u.flat_id);
                                                    setUnitDetails(u);
                                                    handleOpenAssignDeskModal();
                                                  }}
                                                  style={{ fontSize: "11.5px", padding: "5px 12px", flex: 1 }}
                                                >
                                                  <Plus size={13} style={{ marginRight: "4px" }} /> Assign Employee
                                                </Button>
                                                {isSuperAdmin && (
                                                  <Button
                                                    variant="outline"
                                                    size="sm"
                                                    title="Remove Desk"
                                                    onClick={() => handleRemoveDesk(u)}
                                                    style={{
                                                      fontSize: "11px",
                                                      padding: "5px 8px",
                                                      color: "#dc2626",
                                                      borderColor: "#fecaca",
                                                    }}
                                                  >
                                                    <Trash2 size={13} />
                                                  </Button>
                                                )}
                                              </div>
                                            )}
                                          </div>
                                        </div>
                                      );
                                    })}
                                    </div>
                                  )}
                                </div>
                              );
                            }

                            // 1A. MAIN FLOOR VIEW: SHOW DEPARTMENTS (NOT INDIVIDUAL DESKS)!
                            if (floorDepts.length === 0) {
                              return (
                                <div className={styles.emptyDeptFloor}>
                                  <p>No departments or workspaces configured on this floor yet.</p>
                                  <div style={{ display: "flex", gap: "8px", marginTop: "10px" }}>
                                    {isSuperAdmin && (
                                      <Button
                                        variant="secondary"
                                        size="sm"
                                        onClick={() => openAddDeptModal(floor)}
                                      >
                                        + Add Department
                                      </Button>
                                    )}
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() => setUnitModalOpen(true)}
                                    >
                                      Generate Workspaces
                                    </Button>
                                  </div>
                                </div>
                              );
                            }

                            return (
                              <div className={styles.departmentGrid}>
                                {floorDepts.map((dept) => {
                                  const sampleDesks = dept.desks
                                    .map((u) => u.flat_number)
                                    .filter(Boolean);

                                  return (
                                    <div
                                      key={dept.deptName}
                                      className={styles.departmentCard}
                                      onClick={() =>
                                        setSelectedDeptOnFloor((prev) => ({
                                          ...prev,
                                          [floor]: dept.deptName,
                                        }))
                                      }
                                    >
                                      <div className={styles.deptCardHeader}>
                                        <div style={{ display: "flex", gap: "10px", alignItems: "flex-start", flex: 1, minWidth: 0 }}>
                                          <div className={styles.deptIconWrap}>
                                            {dept.deptCode.toUpperCase()}
                                          </div>
                                          <div className={styles.deptTitleInfo}>
                                            <span className={styles.deptCardTitle}>
                                                {dept.deptName.replace(/\s*department$/i, "")} Department
                                              </span>
                                            <div className={styles.deptCardType}>
                                              <span
                                                style={{
                                                  display: "inline-block",
                                                  width: "7px",
                                                  height: "7px",
                                                  borderRadius: "50%",
                                                  background: dept.desks.length === 0 ? "#94a3b8" : dept.occupiedCount > 0 ? "#3b82f6" : "#22c55e",
                                                  marginRight: "5px",
                                                }}
                                              />
                                              <span>
                                                {dept.desks.length} {dept.desks.length === 1 ? "Workspace" : "Workspaces"}
                                              </span>
                                              <span style={{ color: "#cbd5e1", margin: "0 4px" }}>•</span>
                                              <span>
                                                {dept.desks.length === 0
                                                  ? "No desks yet"
                                                  : dept.occupiedCount > 0
                                                    ? `${dept.occupiedCount} Occupied, ${dept.emptyCount} Available`
                                                    : "All Available"}
                                              </span>
                                            </div>
                                          </div>
                                        </div>
                                        {isSuperAdmin && (
                                          <button
                                            type="button"
                                            className={styles.deptRemoveBtn}
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleRemoveDepartmentFromFloor(floor, dept);
                                            }}
                                            title={`Remove ${dept.deptName} Department from ${formatFloorName(floor)}`}
                                          >
                                            <Trash2 size={13} />
                                          </button>
                                        )}
                                      </div>

                                      <div className={styles.deptLeadBox}>
                                        <div className={styles.deptLeadLeft}>
                                          <span className={styles.deptLeadLabel}>Department Lead</span>
                                          <span className={styles.deptLeadName} style={{ color: dept.lead ? "#0f172a" : "#64748b", fontWeight: dept.lead ? 700 : 500 }}>
                                            {dept.lead?.full_name || "Unassigned"}
                                          </span>
                                          {dept.lead && (
                                            <span className={styles.deptLeadDesignation}>
                                              {dept.lead?.role_details?.designation ||
                                                dept.lead?.system_role ||
                                                dept.lead?.primary_role ||
                                                ""}
                                            </span>
                                          )}
                                        </div>
                                        <span
                                          style={{
                                            fontSize: "11px",
                                            fontWeight: 700,
                                            background: "#eff6ff",
                                            color: "#1d4ed8",
                                            border: "1px solid #dbeafe",
                                            padding: "3px 9px",
                                            borderRadius: "12px",
                                            whiteSpace: "nowrap",
                                          }}
                                        >
                                          {dept.staffList.length} Staff
                                        </span>
                                      </div>

                                      {sampleDesks.length > 0 && (
                                        <div className={styles.deptDesksRow}>
                                          {sampleDesks.slice(0, 3).map((d) => (
                                            <span key={d} className={styles.deptDeskPill}>
                                              <Laptop size={11} /> {d}
                                            </span>
                                          ))}
                                          {sampleDesks.length > 3 && (
                                            <span style={{ fontSize: "11px", color: "#64748b", fontWeight: 600 }}>
                                              +{sampleDesks.length - 3} more
                                            </span>
                                          )}
                                        </div>
                                      )}

                                      <div className={styles.deptCardFooter}>
                                        <span>View Desks & Cabins ({dept.desks.length})</span>
                                        <ArrowRight size={14} />
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            );
                          })()
                        ) : isCommercial ? (
                          /* 2. COMMERCIAL: Store / Shop Roster View (Without Desks) */
                          <div className={styles.commercialGrid}>
                            {floorUnits.map((u) => {
                              const assignments = u.assignments || [];
                              const isMultiStorey = String(u.flat_number || "").includes("+") ||
                                String(u.unit_type || "").toLowerCase().includes("multi") ||
                                String(u.unit_type || "").toLowerCase().includes("duplex");

                              const manager = assignments.find(
                                (a) => String(a.user?.primary_role || "").toLowerCase().includes("manager") ||
                                  String(a.user?.role_details?.designation || "").toLowerCase().includes("manager")
                              )?.user || assignments[0]?.user;

                              return (
                                <div
                                  key={u.flat_id}
                                  className={styles.commercialCard}
                                  onClick={() => {
                                    navigate(`/properties/store/${u.flat_id}?propertyId=${propertyId}&towerId=${towerId}`);
                                  }}
                                >
                                  <div className={styles.commCardHeader}>
                                    <div style={{ display: "flex", gap: "10px", alignItems: "flex-start" }}>
                                      <div className={styles.commStoreIcon}>
                                        <Store size={20} />
                                      </div>
                                      <div>
                                        <div className={styles.commCardTitle}>{u.flat_number}</div>
                                        <div style={{ fontSize: "12px", color: "#64748b", marginTop: "2px" }}>
                                          {u.unit_type || "Shop / Retail"}
                                        </div>
                                      </div>
                                    </div>
                                    {isMultiStorey && (
                                      <span className={styles.commMultiBadge}>Multi-Storey</span>
                                    )}
                                  </div>

                                  <div className={styles.commStaffSummary}>
                                    <Users size={14} color="#0f766e" />
                                    <span>
                                      <strong>{assignments.length}</strong> Personnel
                                      {manager ? ` • Mgr: ${manager.full_name}` : ""}
                                    </span>
                                  </div>

                                  <div className={styles.deptCardFooter} style={{ color: "#0f766e" }}>
                                    <span>View Store Roster</span>
                                    <ArrowRight size={14} />
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          /* 3. RESIDENTIAL & HYBRID: Flats Grid with Merging & Occupancy */
                          <div className={styles.unitGrid}>
                            {floorUnits.map((u) => {
                              const rawNumber = String(u.flat_number || "");
                              const isMergedNumber = rawNumber.includes("+") || String(u.unit_type || "") === "Jodi";
                              const displayNumber = isMergedNumber ? rawNumber.split("+").filter(Boolean).join(" + ") : rawNumber;
                              const badgeStyle = getUnitTypeBadgeStyle(u.unit_type);

                              return (
                                <button
                                  key={u.flat_id}
                                  type="button"
                                  onClick={() => {
                                    if (mergeMode) {
                                      toggleMergeSelection(u);
                                    } else {
                                      setSelectedUnitIdForDetails(u.flat_id);
                                      setUnitDetailsModalOpen(true);
                                    }
                                  }}
                                  className={[
                                    styles.unit,
                                    mergeSelected.has(u.flat_id) ? styles.unitSelected : "",
                                  ].filter(Boolean).join(" ")}
                                  title={`Unit ${u.flat_number}`}
                                >
                                  <div className={[styles.unitNumber, isMergedNumber ? styles.unitNumberMerged : ""].filter(Boolean).join(" ")}>
                                    {displayNumber}
                                  </div>
                                  <div
                                    className={styles.unitMeta}
                                    style={{
                                      backgroundColor: badgeStyle.backgroundColor,
                                      color: badgeStyle.color,
                                      border: badgeStyle.border,
                                      borderRadius: "6px",
                                      padding: "2px 6px",
                                      display: "inline-block",
                                      fontWeight: 700,
                                      fontSize: "11px",
                                    }}
                                  >
                                    {u.unit_type || "Unit"}
                                  </div>
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {!floors.length && (
                    <div className={styles.empty}>
                      {isIndustrial
                        ? "No departments or workstations found."
                        : isCommercial
                          ? "No commercial spaces found."
                          : "No units found."}
                    </div>
                  )}
                  {floors.length > 0 && (
                    <div className={styles.metaRowBottom}>
                      <div className={styles.metaText}>
                        Showing Floors <span className={styles.metaTextBold}>{floorOffset + 1}-{Math.min(floorOffset + floorLimit, floors.length)}</span> of <span className={styles.metaTextBold}>{floors.length}</span>
                      </div>
                      <div className={styles.pager}>
                        <div className={styles.pagerSelectWrapper} style={{ minWidth: '120px' }}>
                          <SelectDropdown
                            name="floorLimit"
                            value={floorLimit}
                            onChange={(e) => setFloorLimit(Number(e?.target ? e.target.value : (e?.value ?? e ?? 5)))}
                            options={[
                              { label: "5 / page", value: 5 },
                              { label: "10 / page", value: 10 },
                              { label: "20 / page", value: 20 }
                            ]}
                          />
                        </div>
                        <div className={styles.metaText}>
                          Page <span className={styles.metaTextBold}>{Math.floor(floorOffset / floorLimit) + 1}</span> of <span className={styles.metaTextBold}>{Math.ceil(floors.length / floorLimit)}</span>
                        </div>
                        <div className={styles.pagerButtons}>
                          <Button variant="outline" size="sm" disabled={floorOffset === 0} onClick={() => setFloorOffset((p) => Math.max(0, p - floorLimit))}>Prev</Button>
                          <Button variant="outline" size="sm" disabled={floorOffset + floorLimit >= floors.length} onClick={() => setFloorOffset((p) => p + floorLimit)}>Next</Button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )
            )}
          </div>
        )}
      </div>

      <Modal
        isOpen={towerModalOpen}
        onClose={() => setTowerModalOpen(false)}
        title={isIndustrial ? "Manage Buildings & Departments" : isCommercial ? "Manage Wings & Floors" : "Manage Towers"}
      >
        <TowerForm
          societyId={propertyId}
          towers={towers}
          propertyType={selectedProperty?.society_type}
          initialFloor={unitModalInitialFloor}
          initialDepartment={unitModalInitialDept}
          onSuccess={async () => {
            setTowerModalOpen(false);
            await fetchPropertyDetails();
          }}
        />
      </Modal>
      <Modal
        isOpen={importModalOpen}
        onClose={() => setImportModalOpen(false)}
        title={isIndustrial ? "Import Desks & Cabins" : isCommercial ? "Import Commercial Units" : "Import Units"}
      >
        <BulkImport
          societyId={propertyId}
          onSuccess={async () => {
            setImportModalOpen(false);
            await fetchPropertyDetails();
          }}
        />
      </Modal>
      <Modal
        isOpen={unitModalOpen}
        onClose={() => {
          setUnitModalOpen(false);
          fetchPropertyDetails();
        }}
        size="lg"
        title={
          isIndustrial
            ? "Generate / Update Desks & Cabins"
            : isCommercial
              ? "Generate / Update Commercial Units"
              : "Generate / Update Units"
        }
      >
        <UnitForm
          towers={towers}
          societyId={propertyId}
          propertyName={selectedProperty?.society_name}
          propertyType={selectedProperty?.society_type}
          onSuccess={async () => {
            await fetchPropertyDetails();
          }}
        />
      </Modal>

      {/* Add Department to Specific Property Modal */}
      <Modal
        isOpen={addDeptModalOpen}
        onClose={() => !savingDept && setAddDeptModalOpen(false)}
        title={`Add Department to ${selectedProperty?.society_name || 'Property'}`}
      >
        <form onSubmit={handleAddPropertyDepartment} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <div>
            <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#334155', marginBottom: '6px' }}>
              Target Property
            </label>
            <div style={{
              padding: '10px 12px',
              background: '#f8fafc',
              border: '1px solid #e2e8f0',
              borderRadius: '8px',
              fontSize: '13.5px',
              fontWeight: 600,
              color: '#1e293b',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              <span>{selectedProperty?.society_name || `Property #${propertyId}`}</span>
              <span style={{
                fontSize: '11px',
                padding: '2px 8px',
                background: '#e0e7ff',
                color: '#4338ca',
                borderRadius: '6px',
                fontWeight: 700
              }}>
                {propertyCategory}
              </span>
            </div>
            <span style={{ fontSize: '11.5px', color: '#64748b', marginTop: '4px', display: 'block' }}>
              This department will be linked directly to this property and available in its unit and visitor workflows.
            </span>
          </div>

          {/* Target Building / Wing & Floor Location (Side-by-side) */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <FormField label="Building / Wing" required>
              <SelectDropdown
                name="newDeptTowerId"
                value={newDeptTowerId}
                onChange={(e) => setNewDeptTowerId(e?.target ? e.target.value : (e?.value ?? e ?? ""))}
                options={
                  towers.length > 0
                    ? towers.map((t) => ({
                      label: `${t.tower_name} (ID: #${t.tower_id})`,
                      value: String(t.tower_id),
                    }))
                    : [{ label: "No buildings created yet", value: "" }]
                }
                placeholder="Select building..."
              />
            </FormField>

            <FormField label="Floor Location" required>
              <SelectDropdown
                name="newDeptFloorNumber"
                value={newDeptFloorNumber}
                onChange={(e) => setNewDeptFloorNumber(e?.target ? e.target.value : (e?.value ?? e ?? ""))}
                options={availableFloorsForDept.map((fNo) => ({
                  label: formatFloorName(fNo),
                  value: String(fNo),
                }))}
                placeholder="Select floor..."
              />
            </FormField>
          </div>

          {/* Department Name Selection */}
          <FormField label="Department Name" required>
            <SelectDropdown
              name="newDeptName"
              value={newDeptName}
              onChange={(e) => setNewDeptName(e?.target ? e.target.value : (e?.value ?? e ?? ""))}
              options={departmentDropdownOptions}
              placeholder="Select department..."
              isClearable={false}
            />
          </FormField>

          {/* Auto-Generated Department Code Badge */}
          <FormField label="Auto-Generated Department Code">
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '10px 14px',
              background: '#f8fafc',
              border: '1px solid #cbd5e1',
              borderRadius: '8px',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Lock size={14} color="var(--color-dark-bg)" />
                <span style={{
                  fontFamily: 'monospace',
                  fontWeight: 800,
                  color: 'var(--color-dark-bg)',
                  fontSize: '14px',
                }}>
                  {newDeptCode || "Select department to preview code"}
                </span>
              </div>
              <span style={{
                fontSize: '11px',
                fontWeight: 700,
                color: '#334155',
                background: '#e2e8f0',
                padding: '2px 8px',
                borderRadius: '6px',
                textTransform: 'uppercase',
              }}>
                System Generated
              </span>
            </div>
          </FormField>

          <FormField label="Description" optionalLabel>
            <Textarea
              name="newDeptDescription"
              rows={3}
              placeholder="Operational responsibilities, duties or notes for this department..."
              value={newDeptDescription}
              onChange={(e) => setNewDeptDescription(e?.target ? e.target.value : (e?.value ?? e ?? ""))}
            />
          </FormField>

          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            paddingTop: '14px',
            borderTop: '1px solid #e2e8f0',
            marginTop: '8px'
          }}>
            <Button
              type="button"
              variant="link"
              onClick={() => navigate('/masters/departments')}
              style={{ padding: 0 }}
            >
              Manage All Master Departments &rarr;
            </Button>

            <div style={{ display: 'flex', gap: '10px' }}>
              <Button variant="outline" type="button" onClick={() => setAddDeptModalOpen(false)} disabled={savingDept}>
                Cancel
              </Button>
              <Button variant="primary" type="submit" loading={savingDept}>
                Save Department
              </Button>
            </div>
          </div>
        </form>
      </Modal>

      <Modal
        isOpen={propertyModalOpen}
        onClose={() => {
          setPropertyModalOpen(false);
          setPropertyEditing(null);
        }}
        title={propertyEditing ? "Edit Property" : "Add Property"}
      >
        <SocietyForm
          society={propertyEditing}
          readOnly={false}
          onSuccess={async () => {
            setPropertyModalOpen(false);
            setPropertyEditing(null);
            await fetchProperties();
          }}
          onDeleteSuccess={async () => {
            setPropertyModalOpen(false);
            setPropertyEditing(null);
            await fetchProperties();
          }}
        />
      </Modal>

      <Modal
        isOpen={unitDetailsModalOpen}
        onClose={() => {
          setUnitDetailsModalOpen(false);
          setSelectedUnitIdForDetails("");
          setUnitDetails(null);
          setUnitDetailsError("");
        }}
        title={
          unitDetails?.flat_number
            ? isIndustrial
              ? `Desk / Cabin ${unitDetails.flat_number}`
              : isCommercial
                ? `Commercial Space ${unitDetails.flat_number}`
                : `Unit ${unitDetails.flat_number}`
            : isIndustrial
              ? "Department / Desk Details"
              : isCommercial
                ? "Commercial Space Details"
                : "Unit Details"
        }
      >
        {unitDetailsLoading ? (
          <div style={{ padding: "20px", display: "flex", flexDirection: "column", gap: "12px" }}>
            <div className={styles.slotSkeletonShimmer} style={{ width: "100%", height: "24px", borderRadius: "6px" }} />
            <div className={styles.slotSkeletonShimmer} style={{ width: "75%", height: "20px", borderRadius: "6px" }} />
            <div className={styles.slotSkeletonShimmer} style={{ width: "85%", height: "20px", borderRadius: "6px" }} />
            <div className={styles.slotSkeletonShimmer} style={{ width: "60%", height: "20px", borderRadius: "6px" }} />
          </div>
        ) : unitDetailsError ? (
          <div className={styles.empty}>{unitDetailsError}</div>
        ) : unitDetails ? (
          <div className={styles.modalBody}>
            <div className={styles.modalSection} style={{ padding: "0 20px", gap: 0 }}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "16px 0",
                  borderBottom: "1px solid var(--color-border-soft)",
                }}
              >
                <span style={{ fontSize: "14px", fontWeight: 500, color: "var(--color-text-muted)" }}>
                  Property
                </span>
                <span style={{ fontSize: "14px", fontWeight: 600, color: "var(--color-text-strong)" }}>
                  {selectedProperty?.society_name || unitDetails.society_id || "-"}
                </span>
              </div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "16px 0",
                  borderBottom: "1px solid var(--color-border-soft)",
                }}
              >
                <span style={{ fontSize: "14px", fontWeight: 500, color: "var(--color-text-muted)" }}>
                  {isIndustrial ? "Building" : isCommercial ? "Wing" : "Tower"}
                </span>
                <span style={{ fontSize: "14px", fontWeight: 600, color: "var(--color-text-strong)" }}>
                  {unitDetails.tower_name || selectedTower?.tower_name || unitDetails.tower_id || "-"}
                </span>
              </div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "16px 0",
                  borderBottom: "1px solid var(--color-border-soft)",
                }}
              >
                <span style={{ fontSize: "14px", fontWeight: 500, color: "var(--color-text-muted)" }}>
                  Floor
                </span>
                <span style={{ fontSize: "14px", fontWeight: 600, color: "var(--color-text-strong)" }}>
                  {formatFloorName(unitDetails.floor_number)}
                </span>
              </div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "16px 0",
                  borderBottom: "1px solid var(--color-border-soft)",
                }}
              >
                <span style={{ fontSize: "14px", fontWeight: 500, color: "var(--color-text-muted)" }}>
                  Type
                </span>
                <span
                  style={{
                    display: "inline-flex",
                    padding: "4px 10px",
                    borderRadius: "20px",
                    fontSize: "12px",
                    fontWeight: 700,
                    ...getUnitTypeBadgeStyle(unitDetails.unit_type),
                  }}
                >
                  {unitDetails.unit_type || "-"}
                </span>
              </div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "16px 0",
                  borderBottom: unitDetails.merged_from ? "1px solid var(--color-border-soft)" : "none",
                }}
              >
                <span style={{ fontSize: "14px", fontWeight: 500, color: "var(--color-text-muted)" }}>
                  Status
                </span>
                <span
                  style={{
                    display: "inline-flex",
                    padding: "4px 10px",
                    borderRadius: "20px",
                    fontSize: "12px",
                    fontWeight: 700,
                    textTransform: "capitalize",
                    backgroundColor:
                      unitDetails.status?.toLowerCase() === "available"
                        ? "#dcfce7"
                        : unitDetails.status?.toLowerCase() === "occupied"
                          ? "#dbeafe"
                          : "#f1f5f9",
                    color:
                      unitDetails.status?.toLowerCase() === "available"
                        ? "#15803d"
                        : unitDetails.status?.toLowerCase() === "occupied"
                          ? "#1d4ed8"
                          : "#475569",
                    border: `1px solid ${unitDetails.status?.toLowerCase() === "available"
                        ? "#bbf7d0"
                        : unitDetails.status?.toLowerCase() === "occupied"
                          ? "#bfdbfe"
                          : "#e2e8f0"
                      }`,
                  }}
                >
                  {unitDetails.status || "-"}
                </span>
              </div>
              {unitDetails.merged_from && (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "16px 0",
                  }}
                >
                  <span style={{ fontSize: "14px", fontWeight: 500, color: "var(--color-text-muted)" }}>
                    Merged From
                  </span>
                  <span
                    style={{
                      display: "inline-flex",
                      padding: "4px 10px",
                      borderRadius: "20px",
                      fontSize: "12px",
                      fontWeight: 700,
                      backgroundColor: "#fee2e2",
                      color: "#b91c1c",
                      border: "1px solid #fecaca",
                    }}
                  >
                    {unitDetails.merged_from}
                  </span>
                </div>
              )}
            </div>

            {unitDetails.merged_unit_id && isSuperAdmin && (
              <div style={{ marginTop: "10px" }}>
                <Button
                  variant="danger"
                  onClick={() => {
                    doUnmergeUnit(unitDetails.merged_unit_id);
                    setUnitDetailsModalOpen(false);
                    setSelectedUnitIdForDetails("");
                  }}
                >
                  {isIndustrial ? "Unmerge this Combined Space / Desk" : "Unmerge this Jodi Flat"}
                </Button>
              </div>
            )}

            <div className={styles.modalSection}>
              <div className={styles.modalSectionTitle}>
                {isIndustrial
                  ? "Assigned Personnel & Staff"
                  : isCommercial
                    ? "Assigned Commercial Tenants & Staff"
                    : "Assigned Users"}
              </div>
              {Array.isArray(unitDetails.assignments) && unitDetails.assignments.length ? (
                <div className={styles.tableWrap} style={{ marginTop: 0, border: "none", boxShadow: "none" }}>
                  <table className={styles.table}>
                    <thead>
                      <tr>
                        <th className={styles.th} style={{ background: "transparent" }}>
                          Type
                        </th>
                        <th className={styles.th} style={{ background: "transparent" }}>
                          User
                        </th>
                        <th className={styles.th} style={{ background: "transparent" }}>
                          Contact
                        </th>
                        <th className={styles.th} style={{ background: "transparent" }}>
                          Role
                        </th>
                        {isIndustrial && isSuperAdmin && (
                          <th className={styles.th} style={{ background: "transparent" }}>
                            Actions
                          </th>
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {unitDetails.assignments.map((a) => (
                        <tr key={a.mapping_id} className={styles.trInteractive}>
                          <td className={styles.td}>
                            <span
                              style={{
                                fontWeight: 700,
                                fontSize: "11px",
                                textTransform: "uppercase",
                                letterSpacing: "0.05em",
                                padding: "4px 8px",
                                borderRadius: "6px",
                                backgroundColor:
                                  a.ownership_type?.toLowerCase() === "owner"
                                    ? "#dbeafe"
                                    : a.ownership_type?.toLowerCase() === "tenant"
                                      ? "#fef08a"
                                      : "#f1f5f9",
                                border: `1px solid ${a.ownership_type?.toLowerCase() === "owner"
                                    ? "#bfdbfe"
                                    : a.ownership_type?.toLowerCase() === "tenant"
                                      ? "#fde047"
                                      : "#e2e8f0"
                                  }`,
                                color:
                                  a.ownership_type?.toLowerCase() === "owner"
                                    ? "#1e40af"
                                    : a.ownership_type?.toLowerCase() === "tenant"
                                      ? "#854d0e"
                                      : "#475569",
                              }}
                            >
                              {isIndustrial
                                ? a.ownership_type?.toLowerCase() === "owner"
                                  ? "Director / Owner"
                                  : "Staff / Employee"
                                : isCommercial
                                  ? a.ownership_type?.toLowerCase() === "owner"
                                    ? "Business Owner"
                                    : "Store Tenant"
                                  : a.ownership_type}
                            </span>
                          </td>
                          <td className={styles.td}>
                            {a.user ? (
                              <div style={{ fontWeight: 600 }}>{a.user.full_name}</div>
                            ) : (
                              <div style={{ color: "var(--color-muted)" }}>ID: {a.user_id}</div>
                            )}
                          </td>
                          <td className={styles.td}>
                            {a.user ? (
                              <div style={{ fontSize: "13px", color: "var(--color-text-muted)" }}>
                                <div>{a.user.email_id || "-"}</div>
                                <div>{a.user.mobile_number || "-"}</div>
                              </div>
                            ) : (
                              "-"
                            )}
                          </td>
                          <td className={styles.td}>
                            {a.user ? (
                              <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
                                <span style={{ textTransform: "capitalize", fontWeight: 600, color: "#475569" }}>
                                  {a.user.system_role || a.user.primary_role || "-"}
                                </span>
                                {a.user.role_details?.designation && (
                                  <span style={{ fontSize: "11px", color: "var(--color-dark-bg)", fontWeight: 700 }}>
                                    {a.user.role_details.designation}
                                  </span>
                                )}
                                {a.user.role_details?.desk_code && (
                                  <span
                                    style={{
                                      fontSize: "11px",
                                      fontFamily: "monospace",
                                      color: "#166534",
                                      background: "#f0fdf4",
                                      padding: "1px 4px",
                                      borderRadius: "4px",
                                      width: "fit-content",
                                    }}
                                  >
                                    {a.user.role_details.desk_code}
                                  </span>
                                )}
                              </div>
                            ) : (
                              "-"
                            )}
                          </td>
                          {isIndustrial && isSuperAdmin && (
                            <td className={styles.td}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  icon={<RefreshCw size={12} />}
                                  onClick={() => handleOpenShiftDeskModal(a)}
                                  title="Shift to another desk"
                                >
                                  Shift Desk
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  style={{ color: 'var(--color-danger)' }}
                                  icon={<UserMinus size={13} />}
                                  onClick={() => handleUnassignEmployeeDesk(a.user_id)}
                                  title="Unassign employee"
                                >
                                  Unassign
                                </Button>
                              </div>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                isIndustrial ? (
                  <div className={styles.emptyDeskBanner}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <div className={styles.statusDotGreen} />
                      <div>
                        <div style={{ fontWeight: 700, fontSize: '13.5px', color: '#166534' }}>Desk is Empty & Available</div>
                        <div style={{ fontSize: '12px', color: '#15803d' }}>Ready to be assigned to an employee or staff member.</div>
                      </div>
                    </div>
                    {isSuperAdmin && (
                      <Button
                        variant="primary"
                        size="sm"
                        icon={<UserPlus size={14} />}
                        onClick={handleOpenAssignDeskModal}
                      >
                        Assign Employee
                      </Button>
                    )}
                  </div>
                ) : (
                  <div className={styles.empty} style={{ padding: "10px 0" }}>
                    {isCommercial
                      ? "No tenants assigned to this commercial space."
                      : "No users assigned to this unit."}
                  </div>
                )
              )}
            </div>

            {/* Family Members (Residential only - hidden for Industrial & Commercial) */}
            {!isIndustrial && !isCommercial && unitDetails.assignments?.some((a) => a.user?.family_members?.length > 0) && (
              <div className={styles.modalSection}>
                <div className={styles.modalSectionTitle}>Family Members</div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                  {unitDetails.assignments
                    .flatMap((a) => a.user?.family_members || [])
                    .map((f) => (
                      <div
                        key={f.family_member_id}
                        style={{
                          background: "#f8fafc",
                          border: "1px solid #e2e8f0",
                          padding: "6px 12px",
                          borderRadius: "8px",
                          fontSize: "13px",
                        }}
                      >
                        <strong>{f.member_name}</strong>{" "}
                        <span style={{ color: "#64748b" }}>({f.relation || "Family"})</span>
                      </div>
                    ))}
                </div>
              </div>
            )}

            {/* Vehicles & Parking */}
            {unitDetails.assignments?.some((a) => a.user?.vehicles?.length > 0) && (
              <div className={styles.modalSection}>
                <div className={styles.modalSectionTitle}>
                  {isIndustrial ? "Registered Fleet & Company Vehicles" : "Registered Vehicles & Parking"}
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                  {unitDetails.assignments
                    .flatMap((a) => a.user?.vehicles || [])
                    .map((v) => (
                      <div
                        key={v.vehicle_id}
                        style={{
                          background: "#f8fafc",
                          border: "1px solid #e2e8f0",
                          padding: "6px 12px",
                          borderRadius: "8px",
                          fontSize: "13px",
                        }}
                      >
                        <strong>{v.vehicle_number}</strong>{" "}
                        <span style={{ color: "#64748b" }}>
                          ({v.vehicle_type || "Vehicle"}
                          {v.parking_slot ? ` • Slot ${v.parking_slot}` : ""})
                        </span>
                      </div>
                    ))}
                </div>
              </div>
            )}
          </div>
        ) : null}
      </Modal>
      <InviteUserModal isOpen={inviteModalOpen} onClose={() => setInviteModalOpen(false)} initialSocietyId={selectedProperty?.society_id || selectedProperty?.id} />

      {/* Assign Employee to Desk Modal */}
      <Modal
        isOpen={assignDeskModalOpen}
        onClose={() => !savingDeskAction && setAssignDeskModalOpen(false)}
        title={`Assign Employee to Desk ${unitDetails?.flat_number || ""}`}
      >
        <form onSubmit={handleAssignEmployeeToDesk} style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
          <div>
            <label style={{ display: "block", fontSize: "13px", fontWeight: 600, color: "#334155", marginBottom: "6px" }}>
              Target Desk & Department
            </label>
            <div style={{
              padding: "10px 12px",
              background: "#f8fafc",
              border: "1px solid #e2e8f0",
              borderRadius: "8px",
              fontSize: "13.5px",
              fontWeight: 700,
              color: "var(--color-dark-bg)",
              fontFamily: "monospace",
            }}>
              {unitDetails?.flat_number} ({unitDetails?.unit_type || "Desk"})
            </div>
          </div>

          <FormField label="Select Employee" required>
            <SelectDropdown
              name="staffUserIdToAssign"
              value={staffUserIdToAssign}
              onChange={(e) => setStaffUserIdToAssign(e?.target ? e.target.value : (e?.value ?? e ?? ""))}
              options={
                availableSocietyStaff.length > 0
                  ? availableSocietyStaff.map((s) => ({
                      label: `${s.full_name || s.name} (${s.designation || s.system_role || "Employee"})`,
                      value: String(s.user_id || s.id),
                    }))
                  : [{ label: "No staff available to assign", value: "" }]
              }
              placeholder="Choose employee..."
            />
          </FormField>

          <FormField label="Department Alignment">
            <SelectDropdown
              name="assignDepartment"
              value={assignDepartment}
              onChange={(e) => setAssignDepartment(e?.target ? e.target.value : (e?.value ?? e ?? ""))}
              options={
                availableFlatTypes.length > 0
                  ? availableFlatTypes.filter((t) => t !== "—").map((t) => ({ label: `${t} Dept`, value: t }))
                  : ["HR", "Marketing", "Development", "IT", "Management", "Operations", "Finance", "Sales"].map((t) => ({ label: `${t} Dept`, value: t }))
              }
              placeholder="Select department..."
            />
          </FormField>

          <div>
            <label style={{ display: "block", fontSize: "12px", fontWeight: 700, color: "#166534", marginBottom: "6px" }}>
              Aligned Desk Code (Auto-Updated)
            </label>
            <div style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "9px 12px",
              background: "#f0fdf4",
              border: "1px solid #bbf7d0",
              borderRadius: "8px",
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <Lock size={14} color="#15803d" />
                <span style={{ fontFamily: "monospace", fontSize: "14.5px", fontWeight: 800, color: "#15803d" }}>
                  {alignedDeskCode || unitDetails?.flat_number}
                </span>
              </div>
              {alignedDeskCode && alignedDeskCode !== unitDetails?.flat_number && (
                <span style={{ fontSize: "11px", color: "#166534", fontWeight: 600 }}>
                  Updates from {unitDetails?.flat_number}
                </span>
              )}
            </div>
            <span style={{ fontSize: "11.5px", color: "#64748b", marginTop: "4px", display: "block" }}>
              The desk code automatically updates to align with the employee&apos;s department ({assignDepartment || "department"}).
            </span>
          </div>

          <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px", marginTop: "8px" }}>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setAssignDeskModalOpen(false)}
              disabled={savingDeskAction}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              size="sm"
              loading={savingDeskAction}
              disabled={!staffUserIdToAssign || savingDeskAction}
              icon={<UserPlus size={14} />}
            >
              Assign to Desk
            </Button>
          </div>
        </form>
      </Modal>

      {/* Shift Employee Desk Modal */}
      <Modal
        isOpen={shiftDeskModalOpen}
        onClose={() => !savingDeskAction && setShiftDeskModalOpen(false)}
        title={`Shift ${staffToShift?.user?.full_name || "Employee"} to Another Desk`}
      >
        <form onSubmit={handleShiftEmployeeDesk} style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
          <div>
            <label style={{ display: "block", fontSize: "13px", fontWeight: 600, color: "#334155", marginBottom: "6px" }}>
              Current Desk
            </label>
            <div style={{
              padding: "10px 12px",
              background: "#f8fafc",
              border: "1px solid #e2e8f0",
              borderRadius: "8px",
              fontSize: "13.5px",
              fontWeight: 700,
              color: "var(--color-dark-bg)",
              fontFamily: "monospace",
            }}>
              {unitDetails?.flat_number} (Current)
            </div>
          </div>

          <FormField label="Select New Destination Desk (Empty Only)" required>
            <SelectDropdown
              name="targetShiftFlatId"
              value={targetShiftFlatId}
              onChange={(e) => setTargetShiftFlatId(e?.target ? e.target.value : (e?.value ?? e ?? ""))}
              options={
                flats
                  .filter((f) => String(f.flat_id) !== String(unitDetails?.flat_id) && (!f.assignments || f.assignments.length === 0))
                  .map((f) => ({
                    label: `${f.flat_number} (${f.unit_type || "Desk"} • Floor ${f.floor_number})`,
                    value: String(f.flat_id),
                  }))
              }
              placeholder="Select an empty desk..."
            />
          </FormField>

          <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px", marginTop: "8px" }}>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setShiftDeskModalOpen(false)}
              disabled={savingDeskAction}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              size="sm"
              loading={savingDeskAction}
              disabled={!targetShiftFlatId || savingDeskAction}
              icon={<RefreshCw size={14} />}
            >
              Confirm Shift
            </Button>
          </div>
        </form>
      </Modal>

    </AdminLayout>
  );
};

export default PropertyExplorer;
